import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  FolderKanban,
  Plus,
  TrendingUp,
  AlertTriangle,
  AlertCircle,
  Clock,
  CheckCircle2,
  ChevronRight,
  ListTodo,
  Timer,
  Target,
  BarChart3,
  Receipt,
  Wallet,
  FileText,
  ArrowUpRight,
  ArrowDownLeft,
  Folder,
  MessageSquare,
  Play,
  Square,
  Eye,
  EyeOff,
  Check,
  Phone,
  Mail,
  Calendar,
  X,
  Pencil,
  ListChecks,
  List,
  LayoutGrid,
  MoreVertical,
  Copy,
  Archive,
  Trash2,
} from 'lucide-react';
import { useAppShell, type DrawerSection } from '../layouts/AppShell';
import { useAppNavigate } from '../hooks/useAppNavigate';
import { useTranslation } from '../hooks';
import { usePermissions } from '../contexts/PermissionsContext';
import {
  useProjectsStore,
  useGoogleConnection,
  useActiveTimer,
  useStartTimer,
  useStopTimer,
  seedProjectsMockData,
} from '@business-assistant/shared';
import {
  useBridge,
  calculateBillableRevenue,
} from '@business-assistant/shared';
import { getProjectFolderUrl } from '@business-assistant/shared/services';
import { formatEUR } from '@business-assistant/shared';
import { useSettingsStore } from '@business-assistant/module-settings';
import type {
  Project,
  ProjectStatus,
  Task,
  TaskStatus,
  TimeEntry,
  Milestone,
  ProjectDashboard,
  RiskLevel,
  Invoice,
  ProjectAccountingSnapshot,
  InvoiceDraftProposal,
  ProjectFile,
  Comment,
  TaskList,
  User,
  TeamMember,
} from '@business-assistant/shared/domain';
import { TimesheetGrid } from '../components/projects/TimesheetGrid';
import { MilestonesView } from '../components/projects/MilestonesView';
import { FilesSection } from '../components/projects/FilesSection';
import { FileUploader } from '../components/projects/FileUploader';
import { CommentsPanel } from '../components/projects/CommentsPanel';
import { renderWidgetByType } from '../components/common/registry/ComponentRouter';
import { PrimaryAction, EntityAvatar } from '../components/common/base';
import { FastActionsTrigger } from '../components/actions/FastActions';
import {
  type ProjectFormData,
  type TaskFormData,
  type TimeEntryFormData,
  type MilestoneFormData,
} from '../components/projects';
import { useProjectDeepLink, type ProjectSection as DeepLinkSection } from '../hooks/useProjectDeepLink';
import { MOCK_TASK_CHECKLISTS } from '@business-assistant/shared-data';
import { CallRow, type Call, EmailRow, type Email, EventRow } from '../components/rows';
import type { CalendarEvent } from '@business-assistant/shared/services';
import {
  BoardSection,
  PeopleSection,
  MessagesSection,
  NotebooksSection,
  LinksSection,
  TableSection,
  TimelineSection,
  GanttSection,
  FinanceSection,
  RisksSection,
  SettingsSection,
  TicketsSection,
  ProofsSection,
  FormsSection,
} from '../components/projects/ProjectSections';
import '../components/projects/tasks.css';
import '../components/timer/timer.css';

// =============================================================================
// MOCK NEURAL LINKS DATA (collegamenti tra task e azioni)
// =============================================================================

interface TaskNeuralLinks {
  calls?: number;      // Chiamate effettuate
  emails?: number;     // Email inviate
  events?: number;     // Eventi calendario creati
  messages?: number;   // Messaggi WhatsApp/Chat
  notes?: number;      // Note collegate
  files?: number;      // File allegati
}

/**
 * Mock data showing neural links between tasks and actions.
 * In production, this would come from LinkManager service.
 *
 * Esempi di collegamenti neurali:
 * - tsk-1: 3 chiamate, 2 email, 1 evento, 12 messaggi
 * - tsk-43381593: 7 chiamate fatte, 45 messaggi whatsapp
 */
const MOCK_TASK_NEURAL_LINKS: Record<string, TaskNeuralLinks> = {
  // Tasks from data_handling.ts
  'tsk-1': { calls: 3, emails: 2, events: 1, messages: 12, notes: 2 },
  'tsk-2': { calls: 1, emails: 5, messages: 24, files: 3 },
  'tsk-3': { emails: 1, events: 2, notes: 1 },
  'tsk-4': { calls: 7, messages: 45, notes: 5, files: 2 },
  'tsk-5': { emails: 3, events: 1 },
  'tsk-6': { calls: 2, emails: 8, messages: 15, notes: 3, files: 1 },
  'tsk-7': { calls: 4, emails: 12 },
  'tsk-8': { messages: 67, notes: 8 },
  // Tasks from mockDatabase.ts (Progetti demo)
  'tsk-43381593': { calls: 5, emails: 3, events: 2, messages: 18 },
  'tsk-43381594': { calls: 2, emails: 7, notes: 4 },
  'tsk-43381602': { emails: 4, events: 1, messages: 33, files: 2 },
  'tsk-43407043': { calls: 8, emails: 15, messages: 52, notes: 6 },
  'tsk-43407473': { calls: 1, events: 3 },
  'tsk-43423441': { emails: 9, messages: 28, files: 5 },
  'tsk-43500001': { calls: 3, emails: 2 },
  'tsk-43500002': { messages: 15, notes: 2 },
  'tsk-43450001': { calls: 6, emails: 11, events: 4, messages: 89, notes: 12, files: 7 },
  'tsk-43450002': { calls: 2, emails: 5 },
  'tsk-43460001': { emails: 3, events: 1, messages: 22 },
  'tsk-43470001': { calls: 4, emails: 6, notes: 3 },
  'tsk-43480001': { calls: 1, emails: 2, messages: 8 },
  'tsk-43490001': { emails: 4, events: 2, files: 1 },
};

// =============================================================================
// LINKED ITEMS DATA (actual items for navigation)
// =============================================================================

interface LinkedCall {
  id: string;
  taskId: string;
  contactName: string;
  contactPhone: string;
  date: string;
  duration: string;
  direction: 'in' | 'out';
  summary?: string;
  transcription?: string;
}

interface LinkedEmail {
  id: string;
  taskId: string;
  subject: string;
  from: string;
  to: string;
  date: string;
  preview: string;
  hasAttachments?: boolean;
}

interface LinkedEvent {
  id: string;
  taskId: string;
  title: string;
  date: string;
  time: string;
  duration: string;
  location?: string;
  attendees?: string[];
}

// Mock linked calls - navigable items
const MOCK_LINKED_CALLS: LinkedCall[] = [
  // Calls for tsk-1
  { id: 'call-001', taskId: 'tsk-1', contactName: 'Marco Rossi', contactPhone: '+39 02 1234567', date: '2024-01-15', duration: '12:34', direction: 'out', summary: 'Discussed project timeline', transcription: 'I called Marco to discuss timelines. He confirmed the deadline is January 31st. He asked if we can move the demo up by a week.' },
  { id: 'call-002', taskId: 'tsk-1', contactName: 'Laura Bianchi', contactPhone: '+39 333 4567890', date: '2024-01-16', duration: '05:22', direction: 'in', summary: 'Status update request', transcription: 'Laura called for project status updates. I told her we are at 60% and everything is on track.' },
  { id: 'call-003', taskId: 'tsk-1', contactName: 'Marco Rossi', contactPhone: '+39 02 1234567', date: '2024-01-18', duration: '08:45', direction: 'out', summary: 'Early demo confirmation' },
  // Calls for tsk-4
  { id: 'call-004', taskId: 'tsk-4', contactName: 'Giovanni Verdi', contactPhone: '+39 06 9876543', date: '2024-01-10', duration: '15:00', direction: 'out', summary: 'Contract negotiation' },
  { id: 'call-005', taskId: 'tsk-4', contactName: 'Anna Neri', contactPhone: '+39 02 5551234', date: '2024-01-11', duration: '22:30', direction: 'in', summary: 'Clause review', transcription: 'Anna called to review some contract clauses. We agreed to modify the payment terms.' },
  { id: 'call-006', taskId: 'tsk-4', contactName: 'Giovanni Verdi', contactPhone: '+39 06 9876543', date: '2024-01-12', duration: '10:15', direction: 'out', summary: 'Final draft sent' },
  { id: 'call-007', taskId: 'tsk-4', contactName: 'Company CEO', contactPhone: '+39 02 0001111', date: '2024-01-14', duration: '30:00', direction: 'in', summary: 'Final approval', transcription: 'The CEO approved the contract. We can proceed with signing.' },
  // Calls for tsk-43381593
  { id: 'call-008', taskId: 'tsk-43381593', contactName: 'Client ABC', contactPhone: '+39 02 8889999', date: '2024-01-20', duration: '18:22', direction: 'out', summary: 'Project kickoff' },
  { id: 'call-009', taskId: 'tsk-43381593', contactName: 'Supplier XYZ', contactPhone: '+39 06 1112222', date: '2024-01-21', duration: '09:45', direction: 'in', summary: 'Materials confirmation' },
  { id: 'call-010', taskId: 'tsk-43450001', contactName: 'Team Lead', contactPhone: '+39 333 1234567', date: '2024-01-22', duration: '45:00', direction: 'out', summary: 'Sprint planning', transcription: 'We planned the sprint. 3 story points for refactoring, 5 for the new feature.' },
];

// Mock linked emails - navigable items
const MOCK_LINKED_EMAILS: LinkedEmail[] = [
  // Emails for tsk-1
  { id: 'email-001', taskId: 'tsk-1', subject: 'Re: Q1 Project Update', from: 'marco.rossi@example.com', to: 'me@company.com', date: '2024-01-15', preview: 'I confirm receipt of the document. I have some observations...', hasAttachments: true },
  { id: 'email-002', taskId: 'tsk-1', subject: 'Technical Documentation v2', from: 'me@company.com', to: 'team@example.com', date: '2024-01-17', preview: 'Attached is the new version of the documentation with the requested changes.' },
  // Emails for tsk-2
  { id: 'email-003', taskId: 'tsk-2', subject: 'Business Proposal', from: 'sales@vendor.com', to: 'me@company.com', date: '2024-01-10', preview: 'Dear Client, attached is our best offer...', hasAttachments: true },
  { id: 'email-004', taskId: 'tsk-2', subject: 'Re: Business Proposal', from: 'me@company.com', to: 'sales@vendor.com', date: '2024-01-11', preview: 'Thank you for the proposal. We have some questions...' },
  { id: 'email-005', taskId: 'tsk-2', subject: 'Re: Re: Business Proposal', from: 'sales@vendor.com', to: 'me@company.com', date: '2024-01-12', preview: 'Here are the answers to your questions. Regarding delivery times...' },
  // Emails for tsk-43407043
  { id: 'email-006', taskId: 'tsk-43407043', subject: 'Urgent: Production Issue', from: 'devops@company.com', to: 'team@company.com', date: '2024-01-19', preview: 'We detected a critical issue on the production server...' },
  { id: 'email-007', taskId: 'tsk-43407043', subject: 'Re: Urgent: Issue Resolved', from: 'me@company.com', to: 'devops@company.com', date: '2024-01-19', preview: 'The issue was identified and fixed. It was a memory leak in the cache service.' },
  { id: 'email-008', taskId: 'tsk-43450001', subject: 'Weekly Meeting - Notes', from: 'pm@company.com', to: 'team@company.com', date: '2024-01-22', preview: 'Here are today\'s meeting notes. Action items: 1) Complete review...' },
];

// Mock linked events - navigable items
const MOCK_LINKED_EVENTS: LinkedEvent[] = [
  // Events for tsk-1
  { id: 'event-001', taskId: 'tsk-1', title: 'Product Demo', date: '2024-01-25', time: '14:00', duration: '1h', location: 'Meeting Room A', attendees: ['Marco Rossi', 'Laura Bianchi', 'Team Dev'] },
  // Events for tsk-3
  { id: 'event-002', taskId: 'tsk-3', title: 'Architecture Review', date: '2024-01-20', time: '10:00', duration: '2h', location: 'Online - Teams' },
  { id: 'event-003', taskId: 'tsk-3', title: 'Follow-up Review', date: '2024-01-27', time: '11:00', duration: '30m', location: 'CTO Office' },
  // Events for tsk-43381593
  { id: 'event-004', taskId: 'tsk-43381593', title: 'Kickoff meeting', date: '2024-01-20', time: '09:00', duration: '2h', location: 'Client - Milan', attendees: ['Client ABC', 'PM', 'Tech Lead'] },
  { id: 'event-005', taskId: 'tsk-43381593', title: 'Requirements Workshop', date: '2024-01-22', time: '14:00', duration: '4h', location: 'Online - Zoom' },
  // Events for tsk-43450001
  { id: 'event-006', taskId: 'tsk-43450001', title: 'Sprint Planning', date: '2024-01-22', time: '09:30', duration: '1h 30m', attendees: ['Team Dev', 'PO', 'Scrum Master'] },
  { id: 'event-007', taskId: 'tsk-43450001', title: 'Daily Standup', date: '2024-01-23', time: '09:00', duration: '15m' },
  { id: 'event-008', taskId: 'tsk-43450001', title: 'Retrospective', date: '2024-01-26', time: '16:00', duration: '1h', location: 'Break Room' },
];

// Helper to get linked items for a task
function getLinkedCalls(taskId: string): LinkedCall[] {
  return MOCK_LINKED_CALLS.filter(c => c.taskId === taskId);
}

function getLinkedEmails(taskId: string): LinkedEmail[] {
  return MOCK_LINKED_EMAILS.filter(e => e.taskId === taskId);
}

function getLinkedEvents(taskId: string): LinkedEvent[] {
  return MOCK_LINKED_EVENTS.filter(e => e.taskId === taskId);
}

/** Converts LinkedCall to Call format for reusable CallRow component */
function linkedCallToCall(linkedCall: LinkedCall): Call {
  // Parse duration string "mm:ss" to seconds
  const durationParts = linkedCall.duration.split(':');
  const durationInSeconds = durationParts.length === 2
    ? parseInt(durationParts[0]) * 60 + parseInt(durationParts[1])
    : parseInt(durationParts[0]) * 60;

  return {
    id: linkedCall.id,
    contactName: linkedCall.contactName,
    phone: linkedCall.contactPhone,
    direction: linkedCall.direction,
    duration: durationInSeconds,
    at: linkedCall.date,
    notes: linkedCall.summary,
    answered: true,
  };
}

/** Converts LinkedEmail to Email format for reusable EmailRow component */
function linkedEmailToEmail(linkedEmail: LinkedEmail): Email {
  const isIncoming = linkedEmail.from && !linkedEmail.from.includes('me@');
  return {
    id: linkedEmail.id,
    from: linkedEmail.from,
    to: linkedEmail.to,
    subject: linkedEmail.subject,
    body: linkedEmail.preview,
    direction: isIncoming ? 'in' : 'out',
    at: linkedEmail.date,
    read: true,
    hasAttachments: linkedEmail.hasAttachments,
  };
}

/** Converts LinkedEvent to CalendarEvent format for reusable EventRow component */
function linkedEventToCalendarEvent(linkedEvent: LinkedEvent): CalendarEvent {
  const now = new Date().toISOString();
  return {
    id: linkedEvent.id,
    title: linkedEvent.title,
    startAt: `${linkedEvent.date}T${linkedEvent.time}:00`,
    endAt: undefined, // Could calculate from duration if needed
    allDay: false,
    location: linkedEvent.location,
    attendees: linkedEvent.attendees,
    createdAt: now,
    updatedAt: now,
  };
}

// =============================================================================
// RE-EXPORTS (backward compatibility)
// =============================================================================

export type { ProjectStatus };

export interface ProjectItem {
  id: string;
  title: string;
  status: ProjectStatus;
  statusLabel: string;
  tags: Array<{ label: string; variant?: 'warn' | 'ok' | 'blue' | 'muted' }>;
  time: string;
  progress: number;
  prompt?: string;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const STATUS_LABEL_KEYS: Record<ProjectStatus, string> = {
  'attivo': 'projects.status.active',
  'archiviato': 'projects.status.archived',
};

// Direct labels for the status badge (line 1715 reads this directly,
// without going through i18n). The previous code referenced an
// undefined STATUS_LABELS which crashed the whole detail view
// (`ReferenceError: STATUS_LABELS is not defined`).
const STATUS_LABELS: Record<ProjectStatus, string> = {
  'pianificazione': 'In pianificazione',
  'attivo': 'Attivo',
  'in-pausa': 'In pausa',
  'completato': 'Completato',
  'archiviato': 'Archiviato',
  'sospeso': 'Sospeso',
};

const STATUS_COLORS: Record<ProjectStatus, string> = {
  'attivo': 'ok',
  'archiviato': 'muted',
};

// Health type is already imported via Project type from domain
type ProjectHealth = 'da-iniziare' | 'in-corso' | 'pericolo' | 'ritardo';

const HEALTH_LABEL_KEYS: Record<ProjectHealth, string> = {
  'da-iniziare': 'projects.health.notStarted',
  'in-corso': 'projects.health.inProgress',
  'pericolo': 'projects.health.atRisk',
  'ritardo': 'projects.health.delayed',
};

const HEALTH_COLORS: Record<ProjectHealth, string> = {
  'da-iniziare': 'blue',
  'in-corso': 'ok',
  'pericolo': 'warn',
  'ritardo': 'warn',
};

const TASK_STATUS_LABEL_KEYS: Record<TaskStatus, string> = {
  'todo': 'projects.taskStatus.todo',
  'doing': 'projects.taskStatus.doing',
  'done': 'projects.taskStatus.done',
};

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

/** Health icon (based on project health) */
function HealthIcon({ health }: { health: ProjectHealth }) {
  const icons: Record<ProjectHealth, React.ReactNode> = {
    'in-corso': <TrendingUp size={16} style={{ color: 'var(--success)' }} />,
    'pericolo': <AlertTriangle size={16} style={{ color: 'var(--warn)' }} />,
    'ritardo': <AlertTriangle size={16} style={{ color: 'var(--danger)' }} />,
    'da-iniziare': <Clock size={16} style={{ color: 'var(--info)' }} />,
  };
  return icons[health] || <FolderKanban size={16} />;
}

/** Progress bar */
function ProgressBar({ value, variant = 'default' }: { value: number; variant?: 'default' | 'warn' | 'muted' }) {
  const colors = {
    default: 'var(--brand)',
    warn: 'var(--warn)',
    muted: 'var(--text-muted)',
  };
  return (
    <div style={{ height: '4px', background: 'var(--line)', borderRadius: '2px', overflow: 'hidden' }}>
      <div style={{ width: `${value}%`, height: '100%', background: colors[variant], transition: 'width 0.3s' }} />
    </div>
  );
}

/** Stat badge */
function StatBadge({ icon, label, count, variant, onClick, isActive }: {
  icon: React.ReactNode;
  label: string;
  count: number;
  variant: 'ok' | 'warn' | 'blue';
  onClick?: () => void;
  isActive?: boolean;
}) {
  const styles = {
    ok: { bg: '#dcfce7', color: '#22c55e' },
    warn: { bg: '#fef3c7', color: '#f59e0b' },
    blue: { bg: '#dbeafe', color: '#3b82f6' },
  };
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: '0.375rem',
        padding: '0.25rem 0.625rem', borderRadius: '9999px',
        fontSize: '0.75rem', fontWeight: 500,
        background: styles[variant].bg, color: styles[variant].color,
        cursor: onClick ? 'pointer' : 'default',
        opacity: isActive === false ? 0.6 : 1,
        border: isActive ? `2px solid ${styles[variant].color}` : '2px solid transparent',
        transform: isActive ? 'scale(1.05)' : 'scale(1)',
        transition: 'all 0.2s ease',
      }}
      onMouseEnter={(e) => {
        if (onClick && !isActive) {
          e.currentTarget.style.transform = 'scale(1.05)';
        }
      }}
      onMouseLeave={(e) => {
        if (onClick && !isActive) {
          e.currentTarget.style.transform = 'scale(1)';
        }
      }}
    >
      {icon}
      <span>{count}</span>
      <span style={{ opacity: 0.7 }}>{label}</span>
    </div>
  );
}

/** Section tab button */
function SectionTab({ icon, label, active, onClick }: {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: '0.375rem',
        padding: '0.5rem 0.75rem', border: 'none', borderRadius: '0.5rem',
        background: active ? 'var(--brand)' : 'transparent',
        color: active ? 'white' : 'var(--text-secondary)',
        fontSize: '0.8125rem', fontWeight: 500, cursor: 'pointer',
        transition: 'all 0.15s',
      }}
    >
      {icon}
      {label}
    </button>
  );
}

/**
 * SectionTemplate - Template per sezioni non ancora implementate
 * Ispirato a Teamwork.com - mostra un'anteprima della funzionalità
 */
function SectionTemplate({ title, description, icon, actions, emptyState }: {
  title: string;
  description: string;
  icon: React.ReactNode;
  actions?: Array<{ label: string; onClick: () => void; primary?: boolean }>;
  emptyState?: React.ReactNode;
}) {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '3rem 1.5rem',
      textAlign: 'center',
      minHeight: '400px',
      background: 'var(--bg-base)',
      borderRadius: '0.75rem',
      border: '2px dashed var(--line)',
    }}>
      {/* Icon */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '80px',
        height: '80px',
        borderRadius: '50%',
        background: 'var(--bg-light)',
        color: 'var(--text-muted)',
        marginBottom: '1.5rem',
      }}>
        {icon}
      </div>

      {/* Title */}
      <h2 style={{
        fontSize: '1.5rem',
        fontWeight: 600,
        color: 'var(--text-primary)',
        marginBottom: '0.5rem',
      }}>
        {title}
      </h2>

      {/* Description */}
      <p style={{
        fontSize: '0.9375rem',
        color: 'var(--text-secondary)',
        maxWidth: '400px',
        marginBottom: '1.5rem',
        lineHeight: 1.5,
      }}>
        {description}
      </p>

      {/* Empty state content or actions */}
      {emptyState ? emptyState : actions && actions.length > 0 && (
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'center' }}>
          {actions.map((action, i) => (
            <button
              key={i}
              onClick={action.onClick}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.625rem 1.25rem',
                borderRadius: '0.5rem',
                border: action.primary ? 'none' : '1px solid var(--line)',
                background: action.primary ? 'var(--brand)' : 'var(--bg-base)',
                color: action.primary ? 'white' : 'var(--text-primary)',
                fontSize: '0.875rem',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <Plus size={16} />
              {action.label}
            </button>
          ))}
        </div>
      )}

      {/* Coming soon badge - Note: t function not available here, using static text */}
      <div style={{
        marginTop: '2rem',
        padding: '0.375rem 0.75rem',
        borderRadius: '9999px',
        background: 'var(--bg-light)',
        color: 'var(--text-muted)',
        fontSize: '0.75rem',
        fontWeight: 500,
      }}
        className="coming-soon-badge"
      >
        {/* Use data attribute for i18n key, component using this should translate */}
        Coming soon
      </div>
    </div>
  );
}

// =============================================================================
// PROJECT ROW - Standard row pattern: Inner Actions | Content | Fast Actions
// =============================================================================

interface ProjectRowProps {
  project: Project;
  taskCount: number;
  completedTaskCount: number;
  onClick: () => void;
  onEdit: (e: React.MouseEvent) => void;
  onFastAction: (action: string) => void;
  onMenuAction: (action: 'clone' | 'archive' | 'delete') => void;
}

function ProjectRow({ project, taskCount, completedTaskCount, onClick, onEdit, onFastAction, onMenuAction }: ProjectRowProps) {
  const { t, language } = useTranslation();
  const locale = language === 'it' ? 'it-IT' : 'en-US';
  const [isFavorite, setIsFavorite] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // Health status based on project status and progress
  // Map health field to visual status
  const getHealthStatus = (): 'ok' | 'warn' | 'danger' | 'neutral' => {
    // Archived projects are neutral
    if (project.status === 'archiviato') return 'neutral';
    // Map health values
    switch (project.health) {
      case 'da-iniziare': return 'ok';
      case 'in-corso': return 'ok';
      case 'pericolo': return 'danger';
      case 'ritardo': return 'warn';
      default: return 'ok';
    }
  };

  const healthStatus = getHealthStatus();
  const healthColors = {
    ok: 'var(--success, #22c55e)',
    warn: 'var(--warn, #f59e0b)',
    danger: 'var(--danger, #ef4444)',
    neutral: 'var(--text-muted, #9ca3af)',
  };

  // Task completion percentage
  const taskCompletionPercent = taskCount > 0 ? Math.round((completedTaskCount / taskCount) * 100) : 0;

  // Format dates compactly
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString(locale, { day: '2-digit', month: 'short' });
  };

  return (
    <div
      className="project-row"
      onClick={onClick}
      onDoubleClick={onEdit}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => { setIsHovered(false); setMenuOpen(false); }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.625rem 1rem',
        cursor: 'pointer',
        borderBottom: '1px solid var(--border, #e5e7eb)',
        background: isHovered ? 'var(--bg-hover, #f9fafb)' : 'transparent',
        transition: 'background 0.15s ease',
      }}
    >
      {/* === INNER ACTIONS (left) === */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
        {/* Favorite star */}
        <PrimaryAction
          type="star"
          active={isFavorite}
          size={16}
          onChange={(favorite) => setIsFavorite(favorite)}
        />
        {/* Health status indicator */}
        <div
          title={`${t('projects.health.' + (healthStatus === 'ok' ? 'healthy' : healthStatus === 'warn' ? 'attention' : healthStatus === 'danger' ? 'critical' : 'na'))}`}
          style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: healthColors[healthStatus],
            flexShrink: 0,
          }}
        />
        {/* Avatar */}
        <EntityAvatar
          items={{
            id: project.id,
            name: project.clientName || project.title,
            type: 'company',
            image: project.clientLogo
          }}
          type="company"
          size={28}
        />
      </div>

      {/* === CONTENT (center) === */}
      <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
        {/* Title */}
        <div style={{
          fontSize: '0.875rem',
          fontWeight: 500,
          color: 'var(--text-primary, #111827)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}>
          {project.title}
        </div>
        {/* Meta info: cliente, % task, date, tags */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.375rem',
          fontSize: '0.75rem',
          color: 'var(--text-muted, #6b7280)',
          marginTop: '0.125rem',
          flexWrap: 'wrap',
        }}>
          {/* Client */}
          {project.clientName && (
            <span style={{
              maxWidth: '100px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {project.clientName}
            </span>
          )}
          {/* % task completati */}
          {taskCount > 0 && (
            <>
              {project.clientName && <span style={{ opacity: 0.5 }}>•</span>}
              <span style={{
                color: taskCompletionPercent === 100 ? 'var(--success)' : taskCompletionPercent === 0 ? 'var(--text-muted)' : 'inherit'
              }}>
                {t('projects.percentTasks', { percent: taskCompletionPercent })}
              </span>
            </>
          )}
          {/* Date avvio → fine */}
          {(project.startDate || project.dueAt) && (
            <>
              <span style={{ opacity: 0.5 }}>•</span>
              <span style={{
                color: project.dueAt && new Date(project.dueAt) < new Date() ? 'var(--danger)' : 'inherit'
              }}>
                {formatDate(project.startDate)}
                {project.dueAt && ` → ${formatDate(project.dueAt)}`}
              </span>
            </>
          )}
          {/* Tags */}
          {project.tags && project.tags.length > 0 && (
            <div style={{ display: 'flex', gap: '0.25rem', marginLeft: '0.25rem' }}>
              {project.tags.slice(0, 2).map((tag, i) => (
                <span
                  key={i}
                  style={{
                    padding: '0.0625rem 0.375rem',
                    borderRadius: '9999px',
                    fontSize: '0.625rem',
                    background: 'var(--bg-light, #f3f4f6)',
                    color: 'var(--text-secondary, #4b5563)',
                  }}
                >
                  {tag}
                </span>
              ))}
              {project.tags.length > 2 && (
                <span style={{ fontSize: '0.625rem', color: 'var(--text-muted)' }}>
                  +{project.tags.length - 2}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* === FAST ACTIONS (right) === */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.25rem',
          opacity: isHovered ? 1 : 0,
          transition: 'opacity 0.15s ease',
          flexShrink: 0,
        }}
      >
        {/* Fast actions trigger */}
        <FastActionsTrigger
          entityId={project.id}
          onAction={(action) => onFastAction(action)}
          size="sm"
          position="left"
        />
        {/* Three-dot menu */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen(!menuOpen);
            }}
            style={{
              padding: '0.25rem',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              color: 'var(--text-muted, #6b7280)',
              display: 'flex',
              alignItems: 'center',
              borderRadius: '0.25rem',
            }}
          >
            <MoreVertical size={16} />
          </button>
          {/* Dropdown menu */}
          {menuOpen && (
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: '0.25rem',
                background: 'var(--bg-white, #fff)',
                border: '1px solid var(--border, #e5e7eb)',
                borderRadius: '0.5rem',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                minWidth: '140px',
                zIndex: 100,
                overflow: 'hidden',
              }}
            >
              <button
                onClick={() => { onMenuAction('clone'); setMenuOpen(false); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  fontSize: '0.8125rem',
                  color: 'var(--text-primary)',
                  textAlign: 'left',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-hover, #f3f4f6)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <Copy size={14} /> {t('common.duplicate')}
              </button>
              <button
                onClick={() => { onMenuAction('archive'); setMenuOpen(false); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  fontSize: '0.8125rem',
                  color: 'var(--text-primary)',
                  textAlign: 'left',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-hover, #f3f4f6)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <Archive size={14} /> {t('common.archive')}
              </button>
              <div style={{ height: '1px', background: 'var(--border, #e5e7eb)', margin: '0.25rem 0' }} />
              <button
                onClick={() => { onMenuAction('delete'); setMenuOpen(false); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  fontSize: '0.8125rem',
                  color: 'var(--danger, #ef4444)',
                  textAlign: 'left',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-hover, #f3f4f6)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <Trash2 size={14} /> {t('common.delete')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// PROJECT LIST VIEW
// =============================================================================

function ProjectListView({ onSelectProject }: { onSelectProject: (id: string) => void }) {
  const { t } = useTranslation();
  const { showToast } = useAppShell();
  const projects = useProjectsStore((state) => state.projects);
  const activeViewKey = useProjectsStore((state) => state.activeViewKey);
  const setActiveViewKey = useProjectsStore((state) => state.setActiveViewKey);
  const createProject = useProjectsStore((state) => state.createProject);
  const updateProject = useProjectsStore((state) => state.updateProject);
  const getProjectTasks = useProjectsStore((state) => state.getProjectTasks);

  // Compute filtered projects locally to avoid selector issues
  const filteredProjects = useMemo(() => {
    const key = activeViewKey;

    // Client filter (key format: "client:ClientName")
    if (key.startsWith('client:')) {
      const clientName = key.substring(7);
      return projects.filter((p) => p.clientName === clientName && p.status === 'attivo');
    }

    switch (key) {
      case 'archiviato':
        return projects.filter((p) => p.status === 'archiviato');
      case 'da-iniziare':
        return projects.filter((p) => p.health === 'da-iniziare' && p.status === 'attivo');
      case 'in-corso':
        return projects.filter((p) => p.health === 'in-corso' && p.status === 'attivo');
      case 'pericolo':
        return projects.filter((p) => p.health === 'pericolo' && p.status === 'attivo');
      case 'ritardo':
        return projects.filter((p) => p.health === 'ritardo' && p.status === 'attivo');
      default:
        return projects.filter((p) => p.status === 'attivo');
    }
  }, [projects, activeViewKey]);

  // Helper to get task counts for a project
  const getTaskCounts = (projectId: string) => {
    const tasks = getProjectTasks(projectId);
    const total = tasks.length;
    const completed = tasks.filter(t => t.status === 'done').length;
    return { total, completed };
  };

  const [showProjectForm, setShowProjectForm] = useState(false);
  const [editingProjectId, setEditingProjectId] = useState<string | undefined>();
  const [isSeedingDemo, setIsSeedingDemo] = useState(false);

  // Admin-only: gate the seed-demo button on the role check from PermissionsContext.
  // Pattern: see apps/web/src/layouts/AppShell.tsx for the canonical usePermissions().isAdmin usage.
  const { isAdmin } = usePermissions();

  const handleCreateProject = () => {
    setEditingProjectId(undefined);
    setShowProjectForm(true);
  };

  const handleEditProject = (projectId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent navigation to detail view
    setEditingProjectId(projectId);
    setShowProjectForm(true);
  };

  const handleSeedDemo = async () => {
    if (isSeedingDemo) return;

    // Don't ask for confirmation if the area is already populated — the
    // seedProjectsMockData guard would no-op anyway and the toast would be
    // misleading.
    const currentCount = useProjectsStore.getState().projects.length;
    if (currentCount > 0) {
      showToast(t('projects.toasts.demoAlreadySeeded'));
      return;
    }

    if (!window.confirm(t('projects.confirmSeedDemo'))) return;

    setIsSeedingDemo(true);
    try {
      await seedProjectsMockData();
      // Reload from server so the just-created projects appear immediately.
      // loadFromServer has an 'initialized' short-circuit, so reset the flag
      // first to force a real refetch. Retry once after a short delay: some
      // GraphQL backends have a stale cache right after a mutation and need
      // a beat to commit. If the second fetch is still empty, force a hard
      // reload so the user does not see '3 imported' on top of an empty list.
      useProjectsStore.setState({ initialized: false });
      await useProjectsStore.getState().loadFromServer();
      let count = useProjectsStore.getState().projects.length;

      if (count === 0) {
        console.warn('[SeedDemo] First reload returned 0 projects, retrying after 500ms');
        await new Promise((r) => setTimeout(r, 500));
        useProjectsStore.setState({ initialized: false });
        await useProjectsStore.getState().loadFromServer();
        count = useProjectsStore.getState().projects.length;
      }

      if (count === 0) {
        console.warn(
          '[SeedDemo] Second reload still 0. The 3 projects were likely created on the backend but the in-memory store/cache did not pick them up. Forcing a page reload.'
        );
        // Hard refresh — guaranteed to fetch fresh state from the backend.
        window.location.reload();
        return; // unreachable, but keeps the rest of the function honest
      }

      showToast(t('projects.toasts.demoSeeded', { count }));
    } catch (err) {
      console.error('Demo seed failed', err);
      showToast(t('projects.toasts.demoSeedFailed'));
    } finally {
      setIsSeedingDemo(false);
    }
  };

  // The form's "status" is a progress state (da-iniziare, in-corso...), which the
  // domain model calls health; lifecycle status is 'attivo' for new projects.
  const toProjectPayload = (data: ProjectFormData) => ({
    title: data.title,
    description: data.description,
    clientId: data.clientId,
    clientName: data.clientName,
    health: data.status as unknown as Project['health'],
    dueAt: data.dueAt || undefined,
    budgetTotal: data.budget,
    tags: data.tags,
    team: data.team.map((userId) => ({ userId, role: userId === data.ownerId ? 'owner' : 'member' })),
  });

  const handleSaveProject = async (data: ProjectFormData) => {
    const payload = toProjectPayload(data);
    const result = editingProjectId
      ? await updateProject(editingProjectId, payload)
      : await createProject({
          ...payload,
          status: 'attivo',
          priority: 'media',
          startDate: data.startAt ? new Date(data.startAt).toISOString() : new Date().toISOString(),
        });
    if (result === false || (typeof result === 'object' && result !== null && 'error' in result)) {
      showToast(t('projects.toasts.saveFailed'));
      return;
    }
    showToast(t(editingProjectId ? 'projects.toasts.projectUpdated' : 'projects.toasts.projectCreated'));
    setShowProjectForm(false);
    setEditingProjectId(undefined);
  };

  // Sidebar sections are now computed directly in AppShell from the store

  // Calculate health stats for badges
  const healthStats = useMemo(() => {
    const active = projects.filter((p) => p.status === 'attivo');
    return {
      total: active.length,
      inCorso: active.filter((p) => p.health === 'in-corso').length,
      daIniziare: active.filter((p) => p.health === 'da-iniziare').length,
      pericolo: active.filter((p) => p.health === 'pericolo').length,
      ritardo: active.filter((p) => p.health === 'ritardo').length,
    };
  }, [projects]);

  // Sort filtered projects by title
  const sortedProjects = useMemo(() => {
    return [...filteredProjects].sort((a, b) => a.title.localeCompare(b.title));
  }, [filteredProjects]);

  return (
    <>
      {/* Health stats badges */}
      <div className="section" style={{
        display: 'flex',
        gap: '0.5rem',
        padding: '0.75rem 1rem',
        flexWrap: 'wrap',
        borderBottom: '1px solid var(--border-color)'
      }}>
        <button
          className={`btn btn--sm ${activeViewKey === 'all' ? 'btn--primary' : 'btn--ghost'}`}
          onClick={() => setActiveViewKey('all')}
        >
          {t('common.all')} ({healthStats.total})
        </button>
        <button
          className={`btn btn--sm ${activeViewKey === 'in-corso' ? 'btn--primary' : 'btn--ghost'}`}
          onClick={() => setActiveViewKey('in-corso')}
          style={{ color: activeViewKey === 'in-corso' ? undefined : 'var(--color-success)' }}
        >
          <TrendingUp size={14} /> In progress ({healthStats.inCorso})
        </button>
        <button
          className={`btn btn--sm ${activeViewKey === 'da-iniziare' ? 'btn--primary' : 'btn--ghost'}`}
          onClick={() => setActiveViewKey('da-iniziare')}
          style={{ color: activeViewKey === 'da-iniziare' ? undefined : 'var(--text-muted)' }}
        >
          <Clock size={14} /> Not started ({healthStats.daIniziare})
        </button>
        <button
          className={`btn btn--sm ${activeViewKey === 'pericolo' ? 'btn--primary' : 'btn--ghost'}`}
          onClick={() => setActiveViewKey('pericolo')}
          style={{ color: activeViewKey === 'pericolo' ? undefined : 'var(--color-warning)' }}
        >
          <AlertCircle size={14} /> At risk ({healthStats.pericolo})
        </button>
        <button
          className={`btn btn--sm ${activeViewKey === 'ritardo' ? 'btn--primary' : 'btn--ghost'}`}
          onClick={() => setActiveViewKey('ritardo')}
          style={{ color: activeViewKey === 'ritardo' ? undefined : 'var(--color-danger)' }}
        >
          <AlertTriangle size={14} /> Delayed ({healthStats.ritardo})
        </button>
        {isAdmin && (
          <button
            className="btn btn--sm btn--outline"
            onClick={handleSeedDemo}
            disabled={isSeedingDemo}
            style={{ marginLeft: 'auto' }}
            title={t('projects.seedDemo')}
          >
            {t('projects.seedDemo')}
          </button>
        )}
      </div>

      {/* Empty-state hint: backend has no projects for this user.
          The auto-seed was removed in 9176098 (DEMO_MODE = false because
          backend does not accept the legacy ProjectInput used by the
          seeder). Admins can click to seed demo data; non-admins should
          ask one. Keeps the next session from re-investigating. */}
      {sortedProjects.length === 0 && (
        <div
          className="empty-hint"
          style={{
            padding: 'var(--space-4)',
            margin: 'var(--space-3) 0',
            border: '1px dashed var(--color-border)',
            borderRadius: 'var(--radius)',
            color: 'var(--color-text-muted)',
            fontSize: '14px',
          }}
        >
          {isAdmin ? (
            <>
              {t('projects.emptyHint')}{' '}
              <button
                className="btn btn--sm btn--outline"
                onClick={handleSeedDemo}
                disabled={isSeedingDemo}
                style={{ marginLeft: 8 }}
              >
                {t('projects.seedDemo')}
              </button>
            </>
          ) : (
            t('projects.emptyHintNoAdmin')
          )}
        </div>
      )}

      {/* Project list (sorted by title) */}
      <div className="list">
        {sortedProjects.map((project) => {
          const { total: taskCount, completed: completedTaskCount } = getTaskCounts(project.id);
          // TODO(issue:backend-id-serialization): backend currently returns
          // id: "" for every project row. The mapper in
          // GraphQLProjectsRepository mints a deterministic synthetic id
          // (synth-<slug>) so navigation works; sub-queries fall back to
          // empty arrays via isSyntheticProjectId. When the backend ships
          // a real id, the synth- prefix is gone and everything keeps
          // working unchanged.
          return (
            <ProjectRow
              key={project.id}
              project={project}
              taskCount={taskCount}
              completedTaskCount={completedTaskCount}
              onClick={() => onSelectProject(project.id)}
              onEdit={(e) => handleEditProject(project.id, e)}
              onFastAction={(action) => {
                console.log('Fast action:', action, project.id);
                showToast(`Action: ${action}`);
              }}
              onMenuAction={(action) => {
                console.log('Menu action:', action, project.id);
                if (action === 'clone') {
                  showToast(t('projects.toasts.projectDuplicated'));
                } else if (action === 'archive') {
                  showToast(t('projects.toasts.projectArchived'));
                } else if (action === 'delete') {
                  showToast(t('projects.toasts.projectDeleted'));
                }
              }}
            />
          );
        })}

        {sortedProjects.length === 0 && (
          <div className="section section--padded" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
            <FolderKanban size={32} style={{ marginBottom: '0.5rem', opacity: 0.5 }} />
            <p>{t('projects.noProjectsFor', { view: activeViewKey })}</p>
            <button className="btn btn--primary" style={{ marginTop: '1rem' }} onClick={handleCreateProject}>
              <Plus size={16} /> {t('projects.createProject')}
            </button>
          </div>
        )}
      </div>

      {sortedProjects.length > 0 && (
        <div className="section section--padded">
          <button className="btn btn--outline" onClick={handleCreateProject} style={{ width: '100%' }}>
            <Plus size={16} /> {t('projects.newProject')}
          </button>
        </div>
      )}

      {/* Project Form Modal - Uses centralized ComponentRouter */}
      {showProjectForm && renderWidgetByType('prj', {
        entityId: editingProjectId,
        onClose: () => {
          setShowProjectForm(false);
          setEditingProjectId(undefined);
        },
        onSave: handleSaveProject,
      })}
    </>
  );
}

// =============================================================================
// PROJECT DETAIL VIEW
// =============================================================================

type ProjectSection =
  | 'dashboard'
  | 'tasks'
  | 'board'
  | 'tickets'
  | 'milestones'
  | 'files'
  | 'times'
  | 'messages'
  | 'notebooks'
  | 'links'
  | 'comments'
  | 'people'
  | 'gantt'
  | 'settings'
  | 'proofs'
  | 'forms'
  | 'table'
  | 'timeline'
  | 'finance'
  | 'risks'
  | 'economia';

function ProjectDetailView({ projectId, onBack }: { projectId: string; onBack: () => void }) {
  const { t } = useTranslation();

  // === DEEP LINKING: Widget state from URL ===
  const {
    section: urlSection,
    entityId: urlEntityId,
    entityType: urlEntityType,
    isWidgetOpen,
    isEditMode,
    navigateToSection,
    openEntity,
    closeEntity,
    closeEditMode,
    copyLink,
  } = useProjectDeepLink({ projectIdFromProps: projectId });

  // State for creating NEW entities (not deep-linkable until saved)
  const [isCreatingTask, setIsCreatingTask] = useState(false);
  const [isCreatingTime, setIsCreatingTime] = useState(false);
  const [isCreatingMilestone, setIsCreatingMilestone] = useState(false);
  const [isCreatingProject, setIsCreatingProject] = useState(false);

  // Derive widget visibility: URL (edit) OR local state (create)
  const showTaskForm = (isWidgetOpen && urlEntityType === 'tsk') || isCreatingTask;
  const showTimeForm = (isWidgetOpen && urlEntityType === 'tme') || isCreatingTime;
  const showMilestoneForm = (isWidgetOpen && urlEntityType === 'mst') || isCreatingMilestone;
  // Project form: only from /edit URL or creating new
  const showProjectForm = isEditMode || isCreatingProject;
  const showTicketForm = isWidgetOpen && urlEntityType === 'tkt';
  const editingTaskId = urlEntityType === 'tsk' ? urlEntityId : undefined;
  const editingMilestoneId = urlEntityType === 'mst' ? urlEntityId : undefined;
  const editingTicketId = urlEntityType === 'tkt' ? urlEntityId : undefined;

  // Non-entity modals still use local state
  const [showFileUploader, setShowFileUploader] = useState(false);
  const [showTeamForm, setShowTeamForm] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  // Tasks view mode: toggle between list and board within Tasks section
  const [tasksViewMode, setTasksViewMode] = useState<'list' | 'board'>('list');
  const {
    showToast,
    setHeaderTitleOverride,
    setHeaderBackAction,
    setDrawerSectionsOverride,
    setActiveFilter,
    activeFilters,
    setHeaderBreadcrumbs,
  } = useAppShell();

  const {
    getProject,
    getProjectTasks,
    getProjectTimeEntries,
    getProjectMilestones,
    getProjectDashboard,
    getProjectFiles,
    getProjectSyncStatus,
    createTask,
    updateTask,
    updateProject,
    createTimeEntry,
    updateTimeEntry,
    deleteTimeEntry,
    moveTaskStatus,
    createMilestone,
    toggleMilestone,
    updateProjectProgress,
    uploadFileToDrive,
    syncProjectWithDrive,
    initProjectDriveFolder,
    deleteFile,
  } = useProjectsStore();

  const { data: googleConnection } = useGoogleConnection();
  const isGoogleConnected = googleConnection?.isConnected ?? false;
  const sessionId = googleConnection?.sessionId;

  const project = getProject(projectId);

  // Section labels for breadcrumbs - use translations
  const sectionLabels: Record<string, string> = {
    dashboard: t('projects.sections.dashboard'),
    tasks: t('projects.sections.tasks'),
    board: t('projects.sections.board'),
    tickets: t('projects.sections.tickets'),
    milestones: t('projects.sections.milestones'),
    files: t('projects.sections.files'),
    times: t('projects.sections.times'),
    messages: t('projects.sections.messages'),
    notebooks: t('projects.sections.notebooks'),
    links: t('projects.sections.links'),
    comments: t('projects.sections.comments'),
    people: t('projects.sections.people'),
    gantt: t('projects.sections.gantt'),
    settings: t('projects.sections.settings'),
    proofs: t('projects.sections.proofs'),
    forms: t('projects.sections.forms'),
    table: t('projects.sections.table'),
    timeline: t('projects.sections.timeline'),
    finance: t('projects.sections.finance'),
    risks: t('projects.sections.risks'),
  };

  // Task filter labels for breadcrumbs - use translations
  const taskFilterLabels: Record<string, string> = {
    all: t('projects.taskFilters.all'),
    my: t('projects.taskFilters.my'),
    overdue: t('projects.taskFilters.overdue'),
    today: t('projects.taskFilters.today'),
    backlog: t('projects.taskFilters.backlog'),
    todo: t('projects.taskFilters.todo'),
    'in-progress': t('projects.taskFilters.inProgress'),
    review: t('projects.taskFilters.review'),
    done: t('projects.taskFilters.done'),
  };

  // Determine current section and task filter EARLY (before useEffects that depend on them)
  // URL is the source of truth - urlSection comes from useProjectDeepLink
  const currentSection: ProjectSection = urlSection || 'dashboard';
  const isInTasksSection = currentSection === 'tasks';
  // Task filter can still come from activeFilters for sub-filtering within tasks section
  const taskFilterFromState = activeFilters.progetti || '';
  const currentTaskFilter = taskFilterFromState.startsWith('tasks:')
    ? taskFilterFromState.split(':')[1]
    : 'all';

  // Store navigation functions in refs to avoid useEffect dependency issues
  const navigateToSectionRef = useRef(navigateToSection);
  const onBackRef = useRef(onBack);
  navigateToSectionRef.current = navigateToSection;
  onBackRef.current = onBack;

  // Set header breadcrumbs based on navigation level (using URL-derived currentSection)
  useEffect(() => {
    if (!project) {
      setHeaderBreadcrumbs(null);
      setHeaderTitleOverride(null);
      return;
    }

    const breadcrumbs: { label: string; onClick?: () => void }[] = [];

    // First crumb: "Projects" - goes back to projects list
    breadcrumbs.push({
      label: t('nav.projects'),
      onClick: () => onBackRef.current(),
    });

    if (currentSection !== 'dashboard') {
      // Level 2: Inside a sub-module (e.g., tasks, files, tickets)

      // Second crumb: Project name - goes to project dashboard
      breadcrumbs.push({
        label: project.title,
        onClick: () => navigateToSectionRef.current('dashboard'),
      });

      // Third crumb: Current section
      breadcrumbs.push({
        label: sectionLabels[currentSection] || currentSection,
      });
    } else {
      // Level 2: At project dashboard
      breadcrumbs.push({
        label: project.title,
      });
    }

    setHeaderBreadcrumbs(breadcrumbs);
    setHeaderTitleOverride(null);

    return () => {
      setHeaderBreadcrumbs(null);
    };
  // navigateToSection and onBack are stored in refs, so they don't need to be dependencies
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id, project?.title, currentSection, setHeaderBreadcrumbs, setHeaderTitleOverride]);

  // Set back action based on current navigation level (using URL-derived currentSection)
  useEffect(() => {
    if (!project) return;

    if (currentSection !== 'dashboard') {
      // In a sub-module (tasks, files, etc.) -> go back to dashboard
      setHeaderBackAction(() => () => navigateToSectionRef.current('dashboard'));
    } else {
      // At dashboard -> go back to projects list
      setHeaderBackAction(() => () => onBackRef.current());
    }

    return () => {
      setHeaderBackAction(null);
    };
  // navigateToSection and onBack are stored in refs
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id, currentSection, setHeaderBackAction]);

  // Retrocompatibility: redirect 'board' URL to 'tasks' with board view mode
  useEffect(() => {
    if (currentSection === 'board') {
      setTasksViewMode('board');
      navigateToSectionRef.current('tasks');
    }
  }, [currentSection]);

  // No cleanup needed - AppShell resets drawer sections automatically on view change

  // NOTE: We no longer sync urlSection → activeFilters to avoid circular dependencies
  // Instead, we use urlSection/currentSection directly and navigateToSection for navigation

  // Memoize drawer sections to avoid creating new objects on each render
  const drawerSections = useMemo(() => {
    if (!project) return null;

    const allTasks = getProjectTasks(projectId);
    const allMilestones = getProjectMilestones(projectId);
    const allFiles = getProjectFiles(projectId);
    const allTimeEntries = getProjectTimeEntries(projectId);
    const today = new Date().toDateString();

    // Count active elements (not completed)
    const activeTasksCount = allTasks.filter(t => t.status !== 'done').length;
    const activeMilestonesCount = allMilestones.filter(m => !m.completed).length;

    if (isInTasksSection) {
      return [
        {
          title: t('common.view'),
          items: [
            { id: 'tasks:all', label: t('projects.taskFilters.all'), hint: t('projects.hints.tasks'), count: allTasks.length, active: currentTaskFilter === 'all' },
            { id: 'tasks:my', label: t('projects.taskFilters.my'), hint: t('projects.hints.tasks'), count: allTasks.filter(t => t.assignees?.includes('current-user')).length, active: currentTaskFilter === 'my' },
            { id: 'tasks:overdue', label: t('projects.taskFilters.overdue'), hint: t('projects.hints.tasks'), count: allTasks.filter(t => t.dueAt && new Date(t.dueAt) < new Date() && t.status !== 'done').length, active: currentTaskFilter === 'overdue' },
            { id: 'tasks:today', label: t('projects.taskFilters.today'), hint: t('projects.hints.tasks'), count: allTasks.filter(t => t.dueAt && new Date(t.dueAt).toDateString() === today).length, active: currentTaskFilter === 'today' },
          ],
        },
        {
          title: t('common.status'),
          items: [
            { id: 'tasks:backlog', label: t('projects.taskFilters.backlog'), hint: t('projects.hints.tasks'), count: allTasks.filter(t => t.status === 'backlog').length, active: currentTaskFilter === 'backlog' },
            { id: 'tasks:todo', label: t('projects.taskFilters.todo'), hint: t('projects.hints.tasks'), count: allTasks.filter(t => t.status === 'todo').length, active: currentTaskFilter === 'todo' },
            { id: 'tasks:in-progress', label: t('projects.taskFilters.inProgress'), hint: t('projects.hints.tasks'), count: allTasks.filter(t => t.status === 'in-progress').length, active: currentTaskFilter === 'in-progress' },
            { id: 'tasks:review', label: t('projects.taskFilters.review'), hint: t('projects.hints.tasks'), count: allTasks.filter(t => t.status === 'review').length, active: currentTaskFilter === 'review' },
            { id: 'tasks:done', label: t('projects.taskFilters.done'), hint: t('projects.hints.tasks'), count: allTasks.filter(t => t.status === 'done').length, active: currentTaskFilter === 'done' },
          ],
        },
      ];
    } else {
      // Main project menu with active item counts
      return [
        {
          title: project.title,
          items: [
            { id: 'dashboard', label: t('projects.sections.dashboard'), hint: t('projects.hints.dashboard'), active: currentSection === 'dashboard' },
            { id: 'tasks', label: t('projects.sections.tasks'), hint: t('projects.hints.tasks'), count: activeTasksCount, active: currentSection === 'tasks' },
            { id: 'tickets', label: t('projects.sections.tickets'), hint: t('projects.hints.tickets'), count: 0, active: currentSection === 'tickets' },
            { id: 'milestones', label: t('projects.sections.milestones'), hint: t('projects.hints.milestones'), count: activeMilestonesCount, active: currentSection === 'milestones' },
            { id: 'files', label: t('projects.sections.files'), hint: t('projects.hints.files'), count: allFiles.length, active: currentSection === 'files' },
            { id: 'times', label: t('projects.sections.times'), hint: t('projects.hints.times'), count: allTimeEntries.length, active: currentSection === 'times' },
            { id: 'messages', label: t('projects.sections.messages'), hint: t('projects.hints.messages'), count: 0, active: currentSection === 'messages' },
            { id: 'notebooks', label: t('projects.sections.notebooks'), hint: t('projects.hints.notebooks'), count: 0, active: currentSection === 'notebooks' },
            { id: 'links', label: t('projects.sections.links'), hint: t('projects.hints.links'), count: 0, active: currentSection === 'links' },
            { id: 'comments', label: t('projects.sections.comments'), hint: t('projects.hints.comments'), count: 0, active: currentSection === 'comments' },
            { id: 'people', label: t('projects.sections.people'), hint: t('projects.hints.people'), count: project.teamMembers?.length || 0, active: currentSection === 'people' },
            { id: 'gantt', label: t('projects.sections.gantt'), hint: t('projects.hints.gantt'), active: currentSection === 'gantt' },
            { id: 'settings', label: t('projects.sections.settings'), hint: t('projects.hints.settings'), active: currentSection === 'settings' },
            { id: 'proofs', label: t('projects.sections.proofs'), hint: t('projects.hints.proofs'), count: 0, active: currentSection === 'proofs' },
            { id: 'forms', label: t('projects.sections.forms'), hint: t('projects.hints.forms'), count: 0, active: currentSection === 'forms' },
            { id: 'table', label: t('projects.sections.table'), hint: t('projects.hints.table'), active: currentSection === 'table' },
            { id: 'timeline', label: t('projects.sections.timeline'), hint: t('projects.hints.timeline'), active: currentSection === 'timeline' },
            { id: 'finance', label: t('projects.sections.finance'), hint: t('projects.hints.finance'), active: currentSection === 'finance' },
            { id: 'risks', label: t('projects.sections.risks'), hint: t('projects.hints.risks'), count: 0, active: currentSection === 'risks' },
          ],
        },
      ];
    }
  // Use primitive values to avoid unstable object/function references causing recalculation
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id, project?.title, projectId, isInTasksSection, currentTaskFilter, currentSection]);

  // Track previous drawer sections to avoid infinite loops
  const prevDrawerSectionsRef = useRef<string | null>(null);

  // Update drawer when sections change (with comparison to avoid loops)
  useEffect(() => {
    if (drawerSections) {
      // Serialize to compare - only update if actually changed
      const serialized = JSON.stringify(drawerSections);
      if (prevDrawerSectionsRef.current !== serialized) {
        prevDrawerSectionsRef.current = serialized;
        setDrawerSectionsOverride(drawerSections);
      }
    }
  }, [drawerSections, setDrawerSectionsOverride]);

  if (!project) return null;

  const tasks = getProjectTasks(projectId);
  const timeEntries = getProjectTimeEntries(projectId);
  const milestones = getProjectMilestones(projectId);
  const dashboard = getProjectDashboard(projectId);
  const files = getProjectFiles(projectId);
  const syncStatus = getProjectSyncStatus(projectId);

  // === TASK HANDLERS (URL-driven for edit, local state for create) ===
  const handleAddTask = () => {
    setIsCreatingTask(true);
  };

  const handleEditTask = (taskId: string) => {
    openEntity(taskId, 'tasks');
  };

  // === TICKET HANDLERS (URL-driven) ===
  const handleEditTicket = (ticketId: string) => {
    openEntity(ticketId, 'tickets');
  };

  const handleSaveTask = (data: TaskFormData) => {
    if (editingTaskId) {
      updateTask(editingTaskId, data);
      showToast('Task updated');
      closeEntity(); // Navigate back (remove entityId from URL)
    } else {
      createTask(projectId, data);
      showToast('Task created');
      setIsCreatingTask(false); // Close local create modal
    }
  };

  const handleCloseTaskForm = () => {
    if (isCreatingTask) {
      setIsCreatingTask(false);
    } else {
      closeEntity();
    }
  };

  // === TIME ENTRY HANDLERS ===
  const handleAddTime = () => {
    setIsCreatingTime(true);
  };

  const handleSaveTime = (data: TimeEntryFormData) => {
    const hourlyRate = data.hourlyRate || 0;
    createTimeEntry({
      projectId,
      taskId: data.taskId,
      userId: data.userId,
      userName: data.userName,
      minutes: data.minutes,
      costRate: hourlyRate,
      billableRate: hourlyRate,
      billable: data.billable,
      description: data.description,
      at: data.at,
    });
    setIsCreatingTime(false);
    showToast('Time logged');
  };

  const handleCloseTimeForm = () => {
    if (isCreatingTime) {
      setIsCreatingTime(false);
    } else {
      closeEntity();
    }
  };

  // === MILESTONE HANDLERS (URL-driven for edit, local state for create) ===
  const handleAddMilestone = () => {
    setIsCreatingMilestone(true);
  };

  const handleEditMilestone = (milestoneId: string) => {
    openEntity(milestoneId, 'milestones');
  };

  const handleSaveMilestone = (data: MilestoneFormData) => {
    if (editingMilestoneId) {
      updateMilestone(editingMilestoneId, data);
      showToast('Milestone updated');
      closeEntity();
    } else {
      createMilestone(projectId, data.title, data.dueDate, data.description, data.linkedTaskIds);
      showToast('Milestone created');
      setIsCreatingMilestone(false);
    }
  };

  const handleCloseMilestoneForm = () => {
    if (isCreatingMilestone) {
      setIsCreatingMilestone(false);
    } else {
      closeEntity();
    }
  };

  // File handlers
  const handleAddFile = () => {
    if (!isGoogleConnected) {
      showToast('Connect Google Drive in settings to upload files');
      return;
    }
    setShowFileUploader(true);
  };

  const handleUploadFile = async (file: File): Promise<boolean> => {
    if (!sessionId) return false;
    try {
      const result = await uploadFileToDrive(sessionId, projectId, file, 'usr-current');
      if (result) {
        showToast(`File "${file.name}" uploaded to Drive`);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Upload failed:', error);
      return false;
    }
  };

  const handleDeleteFile = async (fileId: string) => {
    if (confirm(t('projects.files.confirmDelete'))) {
      await deleteFile(fileId, sessionId || undefined);
      showToast(t('projects.files.deleted'));
    }
  };

  const handleSyncWithDrive = async () => {
    if (!sessionId) return;
    setIsSyncing(true);
    try {
      // Initialize folder if needed
      if (!project.driveFolderId) {
        await initProjectDriveFolder(sessionId, projectId);
      }
      // Sync files
      await syncProjectWithDrive(sessionId, projectId, 'usr-current');
      showToast('Sync completed');
    } catch (error) {
      console.error('Sync failed:', error);
      showToast('Error during sync');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleOpenDriveFolder = () => {
    if (project.driveFolderId) {
      window.open(getProjectFolderUrl(project.driveFolderId), '_blank');
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Status badge under title */}
      <div style={{ padding: '0.5rem 1rem', borderBottom: '1px solid var(--line)' }}>
        <span className={`tag tag--${STATUS_COLORS[project.status]}`}>
          {STATUS_LABELS[project.status]}
        </span>
      </div>

      {/* Section content */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {currentSection === 'dashboard' && dashboard && (
          <DashboardSection
            project={project}
            dashboard={dashboard}
            tasks={tasks}
            milestones={milestones}
            onProgressChange={(delta) => updateProjectProgress(projectId, project.progress + delta)}
            onNavigateToSection={(section) => setActiveFilter('progetti', section)}
            onEditProject={() => setShowProjectForm(true)}
            onEditTeam={() => setShowTeamForm(true)}
          />
        )}

        {currentSection === 'tasks' && (
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* View mode toggle */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              padding: '0.5rem 1rem',
              borderBottom: '1px solid var(--border, #e5e7eb)',
              gap: '0.25rem',
            }}>
              <button
                onClick={() => setTasksViewMode('list')}
                title="List view"
                style={{
                  padding: '0.375rem 0.5rem',
                  border: 'none',
                  borderRadius: '0.25rem',
                  background: tasksViewMode === 'list' ? 'var(--brand, #8b5cf6)' : 'transparent',
                  color: tasksViewMode === 'list' ? 'white' : 'var(--text-muted, #6b7280)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  transition: 'all 0.15s ease',
                }}
              >
                <List size={16} />
                Lista
              </button>
              <button
                onClick={() => setTasksViewMode('board')}
                title="Board view"
                style={{
                  padding: '0.375rem 0.5rem',
                  border: 'none',
                  borderRadius: '0.25rem',
                  background: tasksViewMode === 'board' ? 'var(--brand, #8b5cf6)' : 'transparent',
                  color: tasksViewMode === 'board' ? 'white' : 'var(--text-muted, #6b7280)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  transition: 'all 0.15s ease',
                }}
              >
                <LayoutGrid size={16} />
                Bacheca
              </button>
            </div>
            {/* Content based on view mode */}
            <div style={{ flex: 1, overflow: 'auto' }}>
              {tasksViewMode === 'list' ? (
                <TasksSection
                  tasks={tasks}
                  projectId={projectId}
                  projectTitle={project.title}
                  timeEntries={timeEntries}
                  taskFilter={currentTaskFilter}
                  onMoveTask={moveTaskStatus}
                  onAddTask={handleAddTask}
                  onUpdateTask={updateTask}
                  onTaskClick={handleEditTask}
                />
              ) : (
                <div style={{ padding: '1rem' }}>
                  <BoardSection
                    tasks={tasks}
                    onMoveTask={moveTaskStatus}
                    onTaskClick={handleEditTask}
                    onAddTask={handleAddTask}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {currentSection === 'times' && (
          <TimesSection
            timeEntries={timeEntries}
            onAddTime={handleAddTime}
            onUpdateTimeEntry={updateTimeEntry}
            onDeleteTimeEntry={deleteTimeEntry}
          />
        )}

        {currentSection === 'milestones' && (
          <div style={{ padding: '1rem' }}>
            <MilestonesView
              milestones={milestones}
              tasks={tasks}
              onToggleComplete={toggleMilestone}
              onAddMilestone={handleAddMilestone}
              onEditMilestone={handleEditMilestone}
            />
          </div>
        )}

        {currentSection === 'files' && (
          <div style={{ padding: '1rem' }}>
            <FilesSection
              files={files}
              tasks={tasks}
              onAddFile={handleAddFile}
              onDeleteFile={handleDeleteFile}
              onOpenFile={(file) => window.open(file.externalUrl, '_blank')}
              isConnectedToDrive={isGoogleConnected}
              driveFolderId={project.driveFolderId}
              syncStatus={syncStatus}
              isSyncing={isSyncing}
              onSync={handleSyncWithDrive}
              onOpenDriveFolder={handleOpenDriveFolder}
            />
          </div>
        )}

        {currentSection === 'comments' && (
          <div style={{ padding: '1rem', height: 'calc(100% - 2rem)' }}>
            <CommentsPanel
              comments={[]}
              tasks={tasks}
              users={[]}
              currentUserId="usr-current"
              projectId={projectId}
              onSendComment={(text, scope, channels, taskId) => {
                console.log('Send comment:', { text, scope, channels, taskId });
              }}
            />
          </div>
        )}

        {currentSection === 'economia' && (
          <EconomiaSection projectId={projectId} project={project} timeEntries={timeEntries} />
        )}

        {/* Tickets - Support tickets */}
        {currentSection === 'tickets' && (
          <div style={{ padding: '1rem' }}>
            <TicketsSection
              projectId={projectId}
              onTicketClick={handleEditTicket}
            />
          </div>
        )}

        {/* Messages - Project communications */}
        {currentSection === 'messages' && (
          <div style={{ padding: '1rem' }}>
            <MessagesSection projectId={projectId} />
          </div>
        )}

        {/* Notebooks - Notes and documentation */}
        {currentSection === 'notebooks' && (
          <div style={{ padding: '1rem' }}>
            <NotebooksSection projectId={projectId} />
          </div>
        )}

        {/* Links - Related resources */}
        {currentSection === 'links' && (
          <div style={{ padding: '1rem' }}>
            <LinksSection projectId={projectId} />
          </div>
        )}

        {/* People - Team members */}
        {currentSection === 'people' && (
          <div style={{ padding: '1rem' }}>
            <PeopleSection projectId={projectId} />
          </div>
        )}

        {/* Gantt - Timeline view */}
        {currentSection === 'gantt' && (
          <div style={{ padding: '1rem' }}>
            <GanttSection tasks={tasks} onTaskClick={handleEditTask} />
          </div>
        )}

        {/* Settings - Project settings */}
        {currentSection === 'settings' && (
          <div style={{ padding: '1rem' }}>
            <SettingsSection
              project={project}
              onEditProject={() => setShowProjectForm(true)}
            />
          </div>
        )}

        {/* Proofs - Approval workflow */}
        {currentSection === 'proofs' && (
          <div style={{ padding: '1rem' }}>
            <ProofsSection projectId={projectId} />
          </div>
        )}

        {/* Forms - Custom forms */}
        {currentSection === 'forms' && (
          <div style={{ padding: '1rem' }}>
            <FormsSection projectId={projectId} />
          </div>
        )}

        {/* Table - Spreadsheet view */}
        {currentSection === 'table' && (
          <div style={{ padding: '1rem' }}>
            <TableSection tasks={tasks} onTaskClick={handleEditTask} />
          </div>
        )}

        {/* Timeline - Activity timeline */}
        {currentSection === 'timeline' && (
          <div style={{ padding: '1rem' }}>
            <TimelineSection projectId={projectId} />
          </div>
        )}

        {/* Finance - Financial overview */}
        {currentSection === 'finance' && (
          <div style={{ padding: '1rem' }}>
            <FinanceSection projectId={projectId} timeEntries={timeEntries} />
          </div>
        )}

        {/* Risks - Risk management */}
        {currentSection === 'risks' && (
          <div style={{ padding: '1rem' }}>
            <RisksSection projectId={projectId} />
          </div>
        )}
      </div>

      {/* Form Modals - Uses centralized ComponentRouter + URL deep linking */}
      {showTaskForm && renderWidgetByType('tsk', {
        entityId: editingTaskId,
        projectId: projectId,
        onClose: handleCloseTaskForm,
        onSave: handleSaveTask,
      })}

      {showTimeForm && renderWidgetByType('tme', {
        projectId: projectId,
        onClose: handleCloseTimeForm,
        onSave: handleSaveTime,
      })}

      {showMilestoneForm && renderWidgetByType('mst', {
        entityId: editingMilestoneId,
        projectId: projectId,
        onClose: handleCloseMilestoneForm,
        onSave: handleSaveMilestone,
      })}

      {showTicketForm && renderWidgetByType('tkt', {
        entityId: editingTicketId,
        projectId: projectId,
        onClose: closeEntity,
        onSave: (data: unknown) => {
          console.log('Save ticket:', data);
          closeEntity();
        },
      })}

      {showFileUploader && (
        <FileUploader
          isOpen={showFileUploader}
          onClose={() => setShowFileUploader(false)}
          onUpload={handleUploadFile}
          isConnectedToDrive={isGoogleConnected}
          projectTitle={project.title}
        />
      )}

      {/* Project Edit Modal - Uses centralized ComponentRouter */}
      {showProjectForm && renderWidgetByType('prj', {
        entityId: projectId,
        onClose: () => {
          if (isCreatingProject) setIsCreatingProject(false);
          else closeEditMode();
        },
        onSave: (data) => {
          updateProject(projectId, data);
          if (isCreatingProject) setIsCreatingProject(false);
          else closeEditMode();
          showToast(t('projects.toasts.projectUpdated'));
        },
      })}

      {/* Team Edit Modal */}
      {showTeamForm && (
        <TeamEditModal
          team={project.team}
          onSave={(team) => {
            updateProject(projectId, { team });
            setShowTeamForm(false);
            showToast('Team updated');
          }}
          onClose={() => setShowTeamForm(false)}
        />
      )}
    </div>
  );
}

// =============================================================================
// SECTION COMPONENTS
// =============================================================================

interface DashboardSectionProps {
  project: Project;
  dashboard: ProjectDashboard;
  tasks: Task[];
  milestones: Milestone[];
  onProgressChange: (delta: number) => void;
  onNavigateToSection: (section: ProjectSection) => void;
  onEditProject: () => void;
  onEditTeam: () => void;
}

function DashboardSection({
  project,
  dashboard,
  tasks,
  milestones,
  onProgressChange,
  onNavigateToSection,
  onEditProject,
  onEditTeam,
}: DashboardSectionProps) {
  const { t } = useTranslation();
  // The filter callback parameter is renamed `task` so it does not shadow
  // the i18n `t` returned by useTranslation. The original code relied on
  // the shadowing and crashed the detail view (`t is not defined`).
  const tasksDone = tasks.filter(task => task.status === 'done').length;
  const milestonesDone = milestones.filter(m => m.completed).length;

  const formatCurrency = (cents: number) => new Intl.NumberFormat('it-IT', {
    style: 'currency', currency: 'EUR',
  }).format(cents / 100);

  return (
    <div style={{ padding: '1rem' }}>
      {/* Alerts */}
      {dashboard.alerts.length > 0 && (
        <div style={{
          padding: '0.75rem', marginBottom: '1rem',
          background: '#fef3c7', borderRadius: '0.5rem', fontSize: '0.875rem',
        }}>
          <strong style={{ color: '#f59e0b' }}>⚠ Warning:</strong>
          <ul style={{ margin: '0.5rem 0 0 1rem', padding: 0 }}>
            {dashboard.alerts.map((alert, i) => <li key={i}>{alert}</li>)}
          </ul>
        </div>
      )}

      {/* Project Info Header - Clickable */}
      <div
        onClick={onEditProject}
        style={{
          padding: '0.75rem', marginBottom: '1rem',
          background: 'var(--bg)', borderRadius: '0.5rem',
          cursor: 'pointer', transition: 'background 0.15s',
          display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
        }}
        className="clickable-card"
      >
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
            <span style={{ fontWeight: 600 }}>{project.title}</span>
            <Pencil size={12} style={{ color: 'var(--text-muted)' }} />
          </div>
          {project.clientName && (
            <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
              Client: {project.clientName}
            </div>
          )}
          {project.tags.length > 0 && (
            <div style={{ display: 'flex', gap: '0.25rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
              {project.tags.map((tag) => (
                <span
                  key={tag}
                  style={{
                    fontSize: '0.6875rem', padding: '0.125rem 0.375rem',
                    background: 'var(--panel)', borderRadius: '9999px',
                    color: 'var(--text-secondary)',
                  }}
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Progress */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
          <span style={{ fontWeight: 600 }}>Avanzamento</span>
          <span style={{ fontWeight: 600, color: 'var(--brand)' }}>{project.progress}%</span>
        </div>
        <ProgressBar value={project.progress} variant={project.health === 'pericolo' || project.health === 'ritardo' ? 'warn' : 'default'} />
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
          <button onClick={() => onProgressChange(-10)} disabled={project.progress <= 0}
            style={{ flex: 1, padding: '0.375rem', border: '1px solid var(--line)', borderRadius: '0.375rem', background: 'none', cursor: 'pointer' }}>
            -10%
          </button>
          <button onClick={() => onProgressChange(10)} disabled={project.progress >= 100}
            style={{ flex: 1, padding: '0.375rem', border: '1px solid var(--line)', borderRadius: '0.375rem', background: 'none', cursor: 'pointer' }}>
            +10%
          </button>
        </div>
      </div>

      {/* Stats grid - Clickable cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem' }}>
        <ClickableInfoCard
          label="Task completati"
          value={`${tasksDone}/${tasks.length}`}
          onClick={() => onNavigateToSection('tasks')}
        />
        <ClickableInfoCard
          label="Milestone"
          value={`${milestonesDone}/${milestones.length}`}
          onClick={() => onNavigateToSection('milestones')}
        />
        {dashboard.budgetTotal ? (
          <ClickableInfoCard
            label="Budget"
            value={`${formatCurrency(dashboard.budgetUsed || 0)} / ${formatCurrency(dashboard.budgetTotal)}`}
            onClick={() => onNavigateToSection('economia')}
          />
        ) : (
          <ClickableInfoCard
            label="Budget"
            value="Non definito"
            onClick={onEditProject}
            muted
          />
        )}
        {project.dueAt ? (
          <ClickableInfoCard
            label="Due date"
            value={new Date(project.dueAt).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })}
            onClick={onEditProject}
          />
        ) : (
          <ClickableInfoCard
            label="Due date"
            value="Not set"
            onClick={onEditProject}
            muted
          />
        )}
      </div>

      {/* Description - Clickable */}
      <div
        onClick={onEditProject}
        style={{
          marginTop: '1.5rem', padding: '0.75rem',
          background: 'var(--bg)', borderRadius: '0.5rem',
          cursor: 'pointer', transition: 'background 0.15s',
        }}
        className="clickable-card"
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600, marginBottom: '0.5rem' }}>
          Descrizione
          <Pencil size={12} style={{ color: 'var(--text-muted)' }} />
        </div>
        <p style={{ margin: 0, color: project.description ? 'var(--text-secondary)' : 'var(--text-muted)', fontSize: '0.875rem' }}>
          {project.description || 'Click to add a description...'}
        </p>
      </div>

      {/* Contract link */}
      <div
        onClick={() => onNavigateToSection('economia')}
        style={{
          marginTop: '1rem', padding: '0.75rem',
          background: 'var(--bg)', borderRadius: '0.5rem',
          cursor: 'pointer', transition: 'background 0.15s',
          display: 'flex', alignItems: 'center', gap: '0.75rem',
        }}
        className="clickable-card"
      >
        <FileText size={18} style={{ color: 'var(--brand)' }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 500, fontSize: '0.875rem' }}>{t('projects.contractAndInvoices')}</div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            {t('projects.viewEconomicDocs')}
          </div>
        </div>
        <ChevronRight size={16} style={{ color: 'var(--text-muted)' }} />
      </div>

      {/* Team - Clickable */}
      <div
        onClick={onEditTeam}
        style={{
          marginTop: '1rem', padding: '0.75rem',
          background: 'var(--bg)', borderRadius: '0.5rem',
          cursor: 'pointer', transition: 'background 0.15s',
        }}
        className="clickable-card"
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600, marginBottom: '0.5rem' }}>
          Team
          <Pencil size={12} style={{ color: 'var(--text-muted)' }} />
        </div>
        {project.team.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {project.team.map((member, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
                <div style={{
                  width: '28px', height: '28px', borderRadius: '50%',
                  background: 'var(--brand)', color: 'white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.75rem', fontWeight: 600,
                }}>
                  {member.displayName?.[0] || '?'}
                </div>
                <span>{member.displayName}</span>
                <span style={{ color: 'var(--text-muted)' }}>• {member.role}</span>
              </div>
            ))}
          </div>
        ) : (
          <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.875rem' }}>
            {t('projects.team.clickToAdd')}
          </p>
        )}
      </div>
    </div>
  );
}

/** Clickable InfoCard */
function ClickableInfoCard({ label, value, onClick, muted }: { label: string; value: string; onClick: () => void; muted?: boolean }) {
  return (
    <div
      onClick={onClick}
      style={{
        padding: '0.75rem', background: 'var(--bg)', borderRadius: '0.5rem',
        cursor: 'pointer', transition: 'background 0.15s',
      }}
      className="clickable-card"
    >
      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>{label}</div>
      <div style={{ fontWeight: 600, fontSize: '0.875rem', color: muted ? 'var(--text-muted)' : 'var(--text)' }}>{value}</div>
    </div>
  );
}

// =============================================================================
// FAST ACTION MODALS
// =============================================================================

/** Call Modal - Log a phone call + show existing linked calls */
function CallModal({
  task,
  onClose,
  onCall,
  onNavigateToCall,
}: {
  task: Task;
  onClose: () => void;
  onCall: (data: { contactName: string; phoneNumber: string; notes: string; duration: number }) => void;
  onNavigateToCall?: (call: LinkedCall) => void;
}) {
  const { t } = useTranslation();
  const [contactName, setContactName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [duration, setDuration] = useState(5);
  const [showForm, setShowForm] = useState(false);

  // Get existing linked calls for this task
  const linkedCalls = getLinkedCalls(task.id);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onCall({ contactName, phoneNumber, notes, duration });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
        <div className="modal__header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              background: '#dcfce7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#16a34a'
            }}>
              <Phone size={16} />
            </div>
            <div>
              <h3 style={{ margin: 0 }}>{t('projects.calls.title')}</h3>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{task.title}</div>
            </div>
          </div>
          <button className="modal__close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="modal__body" style={{ padding: 0 }}>
          {/* Existing linked calls - NAVIGABLE */}
          {linkedCalls.length > 0 && (
            <div style={{ borderBottom: '1px solid var(--line)' }}>
              <div style={{
                padding: '0.75rem 1rem',
                background: 'var(--bg)',
                fontSize: '0.75rem',
                fontWeight: 600,
                color: 'var(--text-muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                {t('projects.calls.previous', { count: linkedCalls.length })}
              </div>
              <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                {linkedCalls.map((call) => (
                  <CallRow
                    key={call.id}
                    entity={linkedCallToCall(call)}
                    onTitleClick={() => onNavigateToCall?.(call)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* New call form - collapsible if there are existing calls */}
          {linkedCalls.length > 0 && !showForm ? (
            <div
              style={{ padding: '1rem', textAlign: 'center' }}
            >
              <button
                type="button"
                onClick={() => setShowForm(true)}
                style={{
                  background: 'none',
                  border: '1px dashed var(--line)',
                  borderRadius: '0.5rem',
                  padding: '0.75rem 1.5rem',
                  cursor: 'pointer',
                  color: 'var(--accent)',
                  fontSize: '0.875rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <Plus size={16} /> {t('projects.calls.logNew')}
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div style={{ padding: '1rem' }}>
                {linkedCalls.length > 0 && (
                  <div style={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: 'var(--text-muted)',
                    textTransform: 'uppercase',
                    marginBottom: '0.75rem'
                  }}>
                    {t('projects.calls.new')}
                  </div>
                )}

                {/* Contact name */}
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: 500 }}>
                    {t('projects.calls.contact')}
                  </label>
                  <input
                    type="text"
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    placeholder={t('projects.calls.contactPlaceholder')}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid var(--line)',
                      borderRadius: '0.375rem',
                    }}
                  />
                </div>

                {/* Phone + Duration row */}
                <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.75rem' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: 500 }}>
                      {t('projects.calls.phone')}
                    </label>
                    <input
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      placeholder="+39 xxx xxx xxxx"
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid var(--line)',
                        borderRadius: '0.375rem',
                      }}
                    />
                  </div>
                  <div style={{ width: '100px' }}>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: 500 }}>
                      {t('projects.calls.duration')}
                    </label>
                    <input
                      type="number"
                      value={duration}
                      onChange={(e) => setDuration(parseInt(e.target.value) || 0)}
                      min={1}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid var(--line)',
                        borderRadius: '0.375rem',
                      }}
                    />
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: 500 }}>
                    {t('projects.calls.notes')}
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder={t('projects.calls.notesPlaceholder')}
                    rows={2}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid var(--line)',
                      borderRadius: '0.375rem',
                      resize: 'vertical',
                    }}
                  />
                </div>
              </div>

              <div className="modal__footer">
                {linkedCalls.length > 0 && showForm && (
                  <button type="button" className="btn btn--outline" onClick={() => setShowForm(false)}>
                    {t('common.cancel')}
                  </button>
                )}
                {linkedCalls.length === 0 && (
                  <button type="button" className="btn btn--outline" onClick={onClose}>
                    {t('common.close')}
                  </button>
                )}
                <button type="submit" className="btn btn--primary">
                  <Phone size={14} /> {t('projects.calls.log')}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

/** Call Detail Modal - View call details with neural navigation */
function CallDetailModal({
  call,
  task,
  onClose,
  onBack,
  onAddComment,
  onNavigateToTask,
}: {
  call: LinkedCall;
  task?: Task;
  onClose: () => void;
  onBack?: () => void;
  onAddComment?: (comment: string) => void;
  onNavigateToTask?: (taskId: string) => void;
}) {
  const { t } = useTranslation();
  const [newComment, setNewComment] = useState('');
  const [showCommentForm, setShowCommentForm] = useState(false);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '550px' }}>
        <div className="modal__header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {onBack && (
              <button
                onClick={onBack}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '0.25rem',
                  cursor: 'pointer',
                  color: 'var(--text-muted)',
                  display: 'flex',
                }}
              >
                <ArrowDownLeft size={18} style={{ transform: 'rotate(45deg)' }} />
              </button>
            )}
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              background: call.direction === 'out' ? '#dbeafe' : '#dcfce7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: call.direction === 'out' ? '#3b82f6' : '#16a34a',
            }}>
              {call.direction === 'out' ? <ArrowUpRight size={18} /> : <ArrowDownLeft size={18} />}
            </div>
            <div>
              <h3 style={{ margin: 0 }}>{call.contactName}</h3>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                {call.contactPhone} · {call.duration} min · {new Date(call.date).toLocaleDateString(undefined)}
              </div>
            </div>
          </div>
          <button className="modal__close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="modal__body">
          {/* Summary */}
          {call.summary && (
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '0.25rem' }}>
                {t('projects.calls.summary')}
              </div>
              <div style={{ fontSize: '0.875rem' }}>{call.summary}</div>
            </div>
          )}

          {/* Transcription */}
          {call.transcription && (
            <div style={{
              marginBottom: '1rem',
              padding: '0.75rem',
              background: 'var(--bg)',
              borderRadius: '0.5rem',
              borderLeft: '3px solid var(--accent)'
            }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
                {t('projects.calls.transcription')}
              </div>
              <div style={{ fontSize: '0.875rem', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                {call.transcription}
              </div>
            </div>
          )}

          {/* Linked task - clickable for neural navigation */}
          {task && (
            <div
              onClick={() => onNavigateToTask?.(task.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '0.75rem',
                background: 'var(--bg)',
                borderRadius: '0.5rem',
                cursor: 'pointer',
                marginBottom: '1rem',
              }}
            >
              <ListTodo size={18} style={{ color: 'var(--accent)' }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{t('projects.linkedTask')}</div>
                <div style={{ fontWeight: 500 }}>{task.title}</div>
              </div>
              <ChevronRight size={16} style={{ color: 'var(--text-muted)' }} />
            </div>
          )}

          {/* Fast Actions for this call - enable neural navigation */}
          <div style={{
            display: 'flex',
            gap: '0.5rem',
            padding: '0.75rem',
            background: 'var(--bg)',
            borderRadius: '0.5rem',
            marginBottom: '1rem',
          }}>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginRight: 'auto', alignSelf: 'center' }}>
              {t('projects.quickActions')}:
            </div>
            <button
              onClick={() => setShowCommentForm(true)}
              style={{
                padding: '0.5rem 0.75rem',
                border: 'none',
                borderRadius: '0.375rem',
                background: '#f3e8ff',
                color: '#9333ea',
                cursor: 'pointer',
                fontSize: '0.75rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.375rem',
              }}
            >
              <MessageSquare size={14} /> {t('projects.comment')}
            </button>
            <button
              style={{
                padding: '0.5rem 0.75rem',
                border: 'none',
                borderRadius: '0.375rem',
                background: '#fce7f3',
                color: '#ec4899',
                cursor: 'pointer',
                fontSize: '0.75rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.375rem',
              }}
            >
              <Calendar size={14} /> {t('projects.event')}
            </button>
            <button
              style={{
                padding: '0.5rem 0.75rem',
                border: 'none',
                borderRadius: '0.375rem',
                background: '#dcfce7',
                color: '#16a34a',
                cursor: 'pointer',
                fontSize: '0.75rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.375rem',
              }}
            >
              <Phone size={14} /> {t('projects.calls.callBack')}
            </button>
          </div>

          {/* Add comment form */}
          {showCommentForm && (
            <div style={{ marginBottom: '1rem' }}>
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder={t('projects.writeComment')}
                rows={3}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid var(--line)',
                  borderRadius: '0.375rem',
                  resize: 'vertical',
                  marginBottom: '0.5rem',
                }}
                autoFocus
              />
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowCommentForm(false)}
                  style={{
                    padding: '0.375rem 0.75rem',
                    border: '1px solid var(--line)',
                    borderRadius: '0.375rem',
                    background: 'white',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                  }}
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={() => {
                    onAddComment?.(newComment);
                    setNewComment('');
                    setShowCommentForm(false);
                  }}
                  style={{
                    padding: '0.375rem 0.75rem',
                    border: 'none',
                    borderRadius: '0.375rem',
                    background: 'var(--accent)',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                  }}
                >
                  {t('projects.saveComment')}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="modal__footer">
          <button className="btn btn--outline" onClick={onClose}>
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Email Modal - Compose an email */
function EmailModal({
  task,
  onClose,
  onSend,
  onNavigateToEmail,
}: {
  task: Task;
  onClose: () => void;
  onSend: (data: { to: string; subject: string; body: string }) => void;
  onNavigateToEmail?: (email: LinkedEmail) => void;
}) {
  const { t } = useTranslation();
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState(`Re: ${task.title}`);
  const [body, setBody] = useState('');
  const [showForm, setShowForm] = useState(false);

  // Get existing linked emails for this task
  const linkedEmails = getLinkedEmails(task.id);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSend({ to, subject, body });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
        <div className="modal__header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              background: '#dbeafe',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#3b82f6'
            }}>
              <Mail size={16} />
            </div>
            <div>
              <h3 style={{ margin: 0 }}>Email</h3>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{task.title}</div>
            </div>
          </div>
          <button className="modal__close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="modal__body" style={{ padding: 0 }}>
          {/* Existing linked emails - NAVIGABLE */}
          {linkedEmails.length > 0 && (
            <div style={{ borderBottom: '1px solid var(--line)' }}>
              <div style={{
                padding: '0.75rem 1rem',
                background: 'var(--bg)',
                fontSize: '0.75rem',
                fontWeight: 600,
                color: 'var(--text-muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                {t('projects.emails.previous', { count: linkedEmails.length })}
              </div>
              <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                {linkedEmails.map((email) => (
                  <EmailRow
                    key={email.id}
                    entity={linkedEmailToEmail(email)}
                    onTitleClick={() => onNavigateToEmail?.(email)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* New email form - collapsible if there are existing emails */}
          {linkedEmails.length > 0 && !showForm ? (
            <div style={{ padding: '1rem', textAlign: 'center' }}>
              <button
                type="button"
                onClick={() => setShowForm(true)}
                style={{
                  background: 'none',
                  border: '1px dashed var(--line)',
                  borderRadius: '0.5rem',
                  padding: '0.75rem 1.5rem',
                  cursor: 'pointer',
                  color: 'var(--accent)',
                  fontSize: '0.875rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <Plus size={16} /> {t('projects.emails.composeNew')}
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div style={{ padding: '1rem' }}>
                {linkedEmails.length > 0 && (
                  <div style={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: 'var(--text-muted)',
                    textTransform: 'uppercase',
                    marginBottom: '0.75rem'
                  }}>
                    {t('projects.emails.new')}
                  </div>
                )}

                {/* To */}
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: 500 }}>
                    {t('projects.emails.recipient')}
                  </label>
                  <input
                    type="email"
                    value={to}
                    onChange={(e) => setTo(e.target.value)}
                    placeholder={t('projects.emails.recipientPlaceholder')}
                    required
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid var(--line)',
                      borderRadius: '0.375rem',
                    }}
                  />
                </div>

                {/* Subject */}
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: 500 }}>
                    {t('projects.emails.subject')}
                  </label>
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder={t('projects.emails.subjectPlaceholder')}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid var(--line)',
                      borderRadius: '0.375rem',
                    }}
                  />
                </div>

                {/* Body */}
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: 500 }}>
                    {t('projects.emails.message')}
                  </label>
                  <textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder={t('projects.emails.messagePlaceholder')}
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid var(--line)',
                      borderRadius: '0.375rem',
                      resize: 'vertical',
                    }}
                  />
                </div>
              </div>

              <div className="modal__footer">
                <button type="button" className="btn btn--outline" onClick={onClose}>
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn btn--primary" disabled={!to}>
                  <Mail size={14} /> {t('projects.emails.send')}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

/** Event Modal - Create a calendar event + show existing linked events */
function EventModal({
  task,
  onClose,
  onCreate,
  onNavigateToEvent,
}: {
  task: Task;
  onClose: () => void;
  onCreate: (data: { title: string; date: string; time: string; duration: number; description: string; participants: string[] }) => void;
  onNavigateToEvent?: (event: LinkedEvent) => void;
}) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(task.title);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [time, setTime] = useState('10:00');
  const [duration, setDuration] = useState(60);
  const [description, setDescription] = useState('');
  const [participantsInput, setParticipantsInput] = useState('');
  const [showForm, setShowForm] = useState(false);

  // Get existing linked events for this task
  const linkedEvents = getLinkedEvents(task.id);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const participants = participantsInput.split(',').map((p) => p.trim()).filter(Boolean);
    onCreate({ title, date, time, duration, description, participants });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
        <div className="modal__header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              background: '#fce7f3',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#ec4899'
            }}>
              <Calendar size={16} />
            </div>
            <div>
              <h3 style={{ margin: 0 }}>{t('projects.events.title')}</h3>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{task.title}</div>
            </div>
          </div>
          <button className="modal__close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="modal__body" style={{ padding: 0 }}>
          {/* Existing linked events - NAVIGABLE */}
          {linkedEvents.length > 0 && (
            <div style={{ borderBottom: '1px solid var(--line)' }}>
              <div style={{
                padding: '0.75rem 1rem',
                background: 'var(--bg)',
                fontSize: '0.75rem',
                fontWeight: 600,
                color: 'var(--text-muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                {t('projects.events.linked', { count: linkedEvents.length })}
              </div>
              <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                {linkedEvents.map((event) => (
                  <EventRow
                    key={event.id}
                    entity={linkedEventToCalendarEvent(event)}
                    onTitleClick={() => onNavigateToEvent?.(event)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* New event form - collapsible if there are existing events */}
          {linkedEvents.length > 0 && !showForm ? (
            <div style={{ padding: '1rem', textAlign: 'center' }}>
              <button
                type="button"
                onClick={() => setShowForm(true)}
                style={{
                  background: 'none',
                  border: '1px dashed var(--line)',
                  borderRadius: '0.5rem',
                  padding: '0.75rem 1.5rem',
                  cursor: 'pointer',
                  color: 'var(--accent)',
                  fontSize: '0.875rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <Plus size={16} /> {t('projects.events.createNew')}
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div style={{ padding: '1rem' }}>
                {linkedEvents.length > 0 && (
                  <div style={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: 'var(--text-muted)',
                    textTransform: 'uppercase',
                    marginBottom: '0.75rem'
                  }}>
                    {t('projects.events.new')}
                  </div>
                )}

                {/* Title */}
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: 500 }}>
                    {t('projects.events.eventTitle')}
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder={t('projects.events.titlePlaceholder')}
                    required
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid var(--line)',
                      borderRadius: '0.375rem',
                    }}
                  />
                </div>

                {/* Date and Time row */}
                <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.75rem' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: 500 }}>
                      {t('projects.events.date')}
                    </label>
                    <input
                      type="date"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                      required
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid var(--line)',
                        borderRadius: '0.375rem',
                      }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: 500 }}>
                      {t('projects.events.time')}
                    </label>
                    <input
                      type="time"
                      value={time}
                      onChange={(e) => setTime(e.target.value)}
                      required
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid var(--line)',
                        borderRadius: '0.375rem',
                      }}
                    />
                  </div>
                </div>

                {/* Duration */}
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: 500 }}>
                    {t('projects.events.duration')}
                  </label>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {[15, 30, 60, 90, 120].map((mins) => (
                      <button
                        key={mins}
                        type="button"
                        onClick={() => setDuration(mins)}
                        style={{
                          padding: '0.375rem 0.75rem',
                          border: '1px solid var(--line)',
                          borderRadius: '0.375rem',
                          background: duration === mins ? 'var(--brand)' : 'transparent',
                          color: duration === mins ? 'white' : 'var(--text)',
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                        }}
                      >
                        {mins < 60 ? `${mins}m` : `${mins / 60}h`}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Participants */}
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: 500 }}>
                    {t('projects.events.participants')}
                  </label>
                  <input
                    type="text"
                    value={participantsInput}
                    onChange={(e) => setParticipantsInput(e.target.value)}
                    placeholder={t('projects.events.participantsPlaceholder')}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid var(--line)',
                      borderRadius: '0.375rem',
                    }}
                  />
                </div>

                {/* Description */}
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: 500 }}>
                    {t('projects.events.description')}
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder={t('projects.events.descriptionPlaceholder')}
                    rows={2}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid var(--line)',
                      borderRadius: '0.375rem',
                      resize: 'vertical',
                    }}
                  />
                </div>
              </div>

              <div className="modal__footer">
                <button type="button" className="btn btn--outline" onClick={onClose}>
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn btn--primary" disabled={!title}>
                  <Calendar size={14} /> {t('projects.events.create')}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// TASKS SECTION - Hierarchical, Draggable, with Checkboxes and Detail Panel
// =============================================================================

interface TasksSectionProps {
  tasks: Task[];
  projectId: string;
  projectTitle?: string;
  timeEntries?: TimeEntry[];
  taskFilter?: string; // 'all' | 'my' | 'overdue' | 'today' | 'backlog' | 'todo' | 'in-progress' | 'review' | 'done'
  onMoveTask: (id: string, status: TaskStatus) => void;
  onAddTask: () => void;
  onUpdateTask?: (id: string, updates: Partial<Task>) => void;
  onTaskClick?: (taskId: string) => void;
}

function TasksSection({ tasks, projectId, projectTitle, timeEntries = [], taskFilter = 'all', onMoveTask, onAddTask, onUpdateTask, onTaskClick }: TasksSectionProps) {
  const { t } = useTranslation();
  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set());
  const [draggedTaskId, setDraggedTaskId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<{ taskId: string; position: 'before' | 'after' | 'inside' } | null>(null);
  const [hideCompleted, setHideCompleted] = useState(false);
  const isDraggingRef = useRef(false);

  // Fast action modal states
  const [callModalTask, setCallModalTask] = useState<Task | null>(null);
  const [emailModalTask, setEmailModalTask] = useState<Task | null>(null);
  const [eventModalTask, setEventModalTask] = useState<Task | null>(null);

  // Neural navigation state - for viewing call/email/event details
  const [selectedCall, setSelectedCall] = useState<LinkedCall | null>(null);
  const [selectedCallTask, setSelectedCallTask] = useState<Task | null>(null);

  // Timer hooks
  const { data: activeTimer } = useActiveTimer();
  const startTimerMutation = useStartTimer();
  const stopTimerMutation = useStopTimer();
  const { showToast } = useAppShell();

  // Wrapper functions for timer mutations
  const startTimer = useCallback(async (taskId: string, projectId: string, description?: string) => {
    await startTimerMutation.mutateAsync({ taskId, projectId, description });
  }, [startTimerMutation]);

  const stopTimer = useCallback(async () => {
    if (activeTimer) {
      await stopTimerMutation.mutateAsync();
    }
  }, [activeTimer, stopTimerMutation]);

  // Filter tasks based on taskFilter prop
  const filteredTasks = useMemo(() => {
    const today = new Date().toDateString();

    switch (taskFilter) {
      case 'my':
        return tasks.filter(t => t.assignees?.includes('current-user'));
      case 'overdue':
        return tasks.filter(t => t.dueAt && new Date(t.dueAt) < new Date() && t.status !== 'done');
      case 'today':
        return tasks.filter(t => t.dueAt && new Date(t.dueAt).toDateString() === today);
      case 'backlog':
        return tasks.filter(t => t.status === 'backlog');
      case 'todo':
        return tasks.filter(t => t.status === 'todo');
      case 'in-progress':
        return tasks.filter(t => t.status === 'in-progress');
      case 'review':
        return tasks.filter(t => t.status === 'review');
      case 'done':
        return tasks.filter(t => t.status === 'done');
      case 'all':
      default:
        return tasks;
    }
  }, [tasks, taskFilter]);

  const { rootTasks, tasksByParent } = useMemo(() => {
    const byParent = new Map<string | null, Task[]>();
    filteredTasks.forEach((task) => {
      const parentId = task.parentId || null;
      if (!byParent.has(parentId)) byParent.set(parentId, []);
      byParent.get(parentId)!.push(task);
    });
    byParent.forEach((list) => list.sort((a, b) => (a.order || 0) - (b.order || 0)));
    return { rootTasks: byParent.get(null) || [], tasksByParent: byParent };
  }, [filteredTasks]);

  // Timer handlers
  const handleStartTimer = async (taskId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    await startTimer(taskId, projectId);
    const task = tasks.find((t) => t.id === taskId);
    showToast(`Timer avviato: ${task?.title || 'Task'}`);
  };

  const handleStopTimer = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await stopTimer();
  };

  const isTimerActiveForTask = (taskId: string) => {
    return activeTimer?.taskId === taskId;
  };

  // Fast action handlers - open modals
  const handleOpenCallModal = (taskId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const task = tasks.find((t) => t.id === taskId);
    if (task) setCallModalTask(task);
  };

  const handleOpenEmailModal = (taskId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const task = tasks.find((t) => t.id === taskId);
    if (task) setEmailModalTask(task);
  };

  const handleOpenEventModal = (taskId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const task = tasks.find((t) => t.id === taskId);
    if (task) setEventModalTask(task);
  };

  // Auto-expand parents
  useEffect(() => {
    const parentIds = new Set<string>();
    tasks.forEach((t) => { if (t.parentId) parentIds.add(t.parentId); });
    setExpandedTasks(parentIds);
  }, [tasks]);

  const toggleExpand = (taskId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedTasks((prev) => {
      const next = new Set(prev);
      next.has(taskId) ? next.delete(taskId) : next.add(taskId);
      return next;
    });
  };

  const handleToggleComplete = (taskId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const task = tasks.find((t) => t.id === taskId);
    if (task) onMoveTask(taskId, task.status === 'done' ? 'todo' : 'done');
  };

  const handleTaskClick = (taskId: string, e: React.MouseEvent) => {
    e.preventDefault();
    // Only open if not dragging
    if (isDraggingRef.current) {
      console.log('[ProjectsView] Click ignored - drag occurred');
      isDraggingRef.current = false;
      return;
    }
    // Use parent's onTaskClick for deep linking (URL navigation)
    if (onTaskClick) {
      onTaskClick(taskId);
    }
  };

  // Drag handlers
  const handleDragStart = (e: React.DragEvent, taskId: string) => {
    isDraggingRef.current = true;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', taskId);
    setDraggedTaskId(taskId);
  };

  const handleDragOver = (e: React.DragEvent, taskId: string, position: 'before' | 'after' | 'inside') => {
    e.preventDefault();
    if (draggedTaskId === taskId) return;
    const isDescendant = (parentId: string | null, targetId: string): boolean => {
      if (!parentId) return false;
      if (parentId === targetId) return true;
      const parent = tasks.find((t) => t.id === parentId);
      return parent ? isDescendant(parent.parentId || null, targetId) : false;
    };
    if (draggedTaskId && isDescendant(taskId, draggedTaskId)) return;
    setDropTarget({ taskId, position });
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (!draggedTaskId || !dropTarget || !onUpdateTask) {
      setDraggedTaskId(null);
      setDropTarget(null);
      return;
    }
    const { taskId: targetId, position } = dropTarget;
    const targetTask = tasks.find((t) => t.id === targetId);
    if (!targetTask) return;

    let newParentId: string | null = null;
    let newOrder = 0;

    if (position === 'inside') {
      newParentId = targetId;
      const children = tasksByParent.get(targetId) || [];
      newOrder = children.length > 0 ? Math.max(...children.map((t) => t.order || 0)) + 1 : 0;
      setExpandedTasks((prev) => new Set([...prev, targetId]));
    } else {
      newParentId = targetTask.parentId || null;
      const siblings = tasksByParent.get(newParentId) || [];
      const idx = siblings.findIndex((t) => t.id === targetId);
      if (position === 'before') {
        newOrder = idx === 0 ? (targetTask.order || 0) - 1 : ((siblings[idx - 1].order || 0) + (targetTask.order || 0)) / 2;
      } else {
        newOrder = idx === siblings.length - 1 ? (targetTask.order || 0) + 1 : ((targetTask.order || 0) + (siblings[idx + 1].order || 0)) / 2;
      }
    }
    onUpdateTask(draggedTaskId, { parentId: newParentId, order: newOrder });
    setDraggedTaskId(null);
    setDropTarget(null);
  };

  // Status counts
  const statusCounts = {
    todo: tasks.filter((t) => t.status === 'todo').length,
    doing: tasks.filter((t) => t.status === 'doing').length,
    done: tasks.filter((t) => t.status === 'done').length,
  };

  // Render task row
  const renderTaskRow = (task: Task, depth: number = 0) => {
    const children = tasksByParent.get(task.id) || [];
    const hasChildren = children.length > 0;
    const isExpanded = expandedTasks.has(task.id);
    const isDragging = draggedTaskId === task.id;
    const isDropTargetTask = dropTarget?.taskId === task.id;
    const depthClass = depth > 0 ? `task-row--depth-${Math.min(depth, 4)}` : '';

    return (
      <div key={task.id}>
        {isDropTargetTask && dropTarget?.position === 'before' && (
          <div className="task-row__drop-indicator" />
        )}

        <div
          className={`task-row ${depthClass} ${isDragging ? 'task-row--dragging' : ''} ${isDropTargetTask && dropTarget?.position === 'inside' ? 'task-row--drop-inside' : ''} ${task.status === 'done' ? 'task-row--done' : ''}`}
          draggable
          onClick={(e) => handleTaskClick(task.id, e)}
          onDragStart={(e) => handleDragStart(e, task.id)}
          onDragOver={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const y = e.clientY - rect.top;
            const h = rect.height;
            handleDragOver(e, task.id, y < h * 0.25 ? 'before' : y > h * 0.75 ? 'after' : 'inside');
          }}
          onDragLeave={() => setDropTarget(null)}
          onDrop={handleDrop}
          onDragEnd={() => { setDraggedTaskId(null); setDropTarget(null); setTimeout(() => { isDraggingRef.current = false; }, 50); }}
        >
          <div className="task-row__drag">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="5" cy="5" r="2" /><circle cx="12" cy="5" r="2" />
              <circle cx="5" cy="12" r="2" /><circle cx="12" cy="12" r="2" />
              <circle cx="5" cy="19" r="2" /><circle cx="12" cy="19" r="2" />
            </svg>
          </div>

          <button
            className={`task-row__expand ${!hasChildren ? 'task-row__expand--hidden' : ''}`}
            onClick={(e) => toggleExpand(task.id, e)}
          >
            <ChevronRight size={14} className={`task-row__expand-icon ${isExpanded ? 'task-row__expand-icon--expanded' : ''}`} />
          </button>

          <button
            className={`task-row__checkbox ${task.status === 'done' ? 'task-row__checkbox--checked' : ''}`}
            onClick={(e) => handleToggleComplete(task.id, e)}
          >
            {task.status === 'done' && <CheckCircle2 size={12} className="task-row__checkbox-icon" />}
          </button>

          <div className="task-row__content">
            <div className={`task-row__title ${task.status === 'done' ? 'task-row__title--done' : ''}`}>
              {task.title}
            </div>
            {task.dueAt && (
              <div className="task-row__meta">
                {new Date(task.dueAt).toLocaleDateString(undefined, { day: '2-digit', month: 'short' })}
              </div>
            )}
          </div>

          {/* Timer button */}
          {task.status !== 'done' && (
            <button
              className={`task-row__timer-btn ${isTimerActiveForTask(task.id) ? 'task-row__timer-btn--active' : ''}`}
              onClick={(e) => isTimerActiveForTask(task.id) ? handleStopTimer(e) : handleStartTimer(task.id, e)}
              title={isTimerActiveForTask(task.id) ? 'Stop timer' : 'Avvia timer'}
            >
              {isTimerActiveForTask(task.id) ? <Square size={10} /> : <Play size={10} />}
            </button>
          )}

          {/* Fast actions with neural link counters */}
          {(() => {
            const links = MOCK_TASK_NEURAL_LINKS[task.id] || {};
            return (
              <div className="task-row__actions">
                <button
                  className={`task-row__action-btn task-row__action-btn--call ${links.calls ? 'task-row__action-btn--has-links' : ''}`}
                  onClick={(e) => handleOpenCallModal(task.id, e)}
                  title={links.calls ? `${links.calls} linked calls` : 'Call'}
                >
                  <Phone size={12} />
                  {links.calls && links.calls > 0 && (
                    <span className="task-row__action-counter task-row__action-counter--call">
                      {links.calls > 99 ? '99+' : links.calls}
                    </span>
                  )}
                </button>
                <button
                  className={`task-row__action-btn task-row__action-btn--email ${links.emails ? 'task-row__action-btn--has-links' : ''}`}
                  onClick={(e) => handleOpenEmailModal(task.id, e)}
                  title={links.emails ? `${links.emails} linked emails` : 'Send email'}
                >
                  <Mail size={12} />
                  {links.emails && links.emails > 0 && (
                    <span className="task-row__action-counter task-row__action-counter--email">
                      {links.emails > 99 ? '99+' : links.emails}
                    </span>
                  )}
                </button>
                <button
                  className={`task-row__action-btn task-row__action-btn--event ${links.events ? 'task-row__action-btn--has-links' : ''}`}
                  onClick={(e) => handleOpenEventModal(task.id, e)}
                  title={links.events ? t('projects.events.linkedCount', { count: links.events }) : t('projects.events.create')}
                >
                  <Calendar size={12} />
                  {links.events && links.events > 0 && (
                    <span className="task-row__action-counter task-row__action-counter--event">
                      {links.events > 99 ? '99+' : links.events}
                    </span>
                  )}
                </button>
              </div>
            );
          })()}

          {/* Checklist badge */}
          {MOCK_TASK_CHECKLISTS[task.id] && (() => {
            const checklist = MOCK_TASK_CHECKLISTS[task.id];
            const done = checklist.filter(c => c.done).length;
            const total = checklist.length;
            const isComplete = done === total;
            return (
              <span
                className={`task-row__badge ${isComplete ? 'task-row__badge--done' : 'task-row__badge--todo'}`}
                title={t('projects.tasks.checklistProgress', { done, total })}
              >
                <ListChecks size={10} style={{ marginRight: '3px' }} />
                {done}/{total}
              </span>
            );
          })()}

          {hasChildren && <span className="task-row__badge task-row__badge--subtasks">{children.length} subtask</span>}
          <span className={`task-row__badge task-row__badge--${task.status}`}>{TASK_STATUS_LABELS[task.status]}</span>
        </div>

        {isDropTargetTask && dropTarget?.position === 'after' && (
          <div className="task-row__drop-indicator" />
        )}

        {hasChildren && isExpanded && (
          <div className="task-row__children">
            <div className="task-row__children-line" />
            {children.map((child) => renderTaskRow(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="tasks">
      <div className="tasks__header">
        <div className="tasks__stats">
          <span className="tasks__stat">{statusCounts.todo} {t('projects.tasks.toDo')}</span>
          <span className="tasks__stat tasks__stat--doing">{statusCounts.doing} {t('projects.tasks.inProgress')}</span>
          <span className="tasks__stat tasks__stat--done">{statusCounts.done} {t('projects.tasks.completed')}</span>
        </div>
        <div className="tasks__header-actions">
          <button
            className={`tasks__toggle ${hideCompleted ? 'tasks__toggle--active' : ''}`}
            onClick={() => setHideCompleted(!hideCompleted)}
            title={hideCompleted ? t('projects.tasks.showCompleted') : t('projects.tasks.hideCompleted')}
          >
            <span className="tasks__toggle-icon">
              {hideCompleted && <Check size={10} />}
            </span>
            {hideCompleted ? <EyeOff size={14} /> : <Eye size={14} />}
            <span>{hideCompleted ? t('projects.tasks.hidden') : t('projects.tasks.hide')}</span>
          </button>
          <button onClick={onAddTask} className="btn btn--primary btn--sm">
            <Plus size={14} /> {t('projects.tasks.newTask')}
          </button>
        </div>
      </div>

      <div className="tasks__body">
        {tasks.length === 0 ? (
          <div className="tasks__empty">
            <ListTodo size={32} className="tasks__empty-icon" />
            <p>{t('projects.tasks.noTasks')}</p>
          </div>
        ) : (
          rootTasks.map((task) => renderTaskRow(task, 0))
        )}
      </div>

      {/* Call Modal */}
      {callModalTask && !selectedCall && (
        <CallModal
          task={callModalTask}
          onClose={() => setCallModalTask(null)}
          onCall={(data) => {
            showToast(t('projects.toasts.callLogged', { name: data.contactName || t('projects.calls.contact') }));
            setCallModalTask(null);
          }}
          onNavigateToCall={(call) => {
            // Neural navigation: open call detail
            setSelectedCall(call);
            setSelectedCallTask(callModalTask);
          }}
        />
      )}

      {/* Call Detail Modal - Neural navigation view */}
      {selectedCall && (
        <CallDetailModal
          call={selectedCall}
          task={selectedCallTask || undefined}
          onClose={() => {
            setSelectedCall(null);
            setSelectedCallTask(null);
            setCallModalTask(null);
          }}
          onBack={() => {
            // Go back to call list
            setSelectedCall(null);
          }}
          onAddComment={(comment) => {
            showToast(t('projects.toasts.commentAdded'));
            console.log('Comment added to call:', selectedCall.id, comment);
          }}
          onNavigateToTask={(taskId) => {
            // Neural navigation: close call and navigate to task via deep link
            setSelectedCall(null);
            setSelectedCallTask(null);
            setCallModalTask(null);
            if (onTaskClick) {
              onTaskClick(taskId);
            }
          }}
        />
      )}

      {/* Email Modal */}
      {emailModalTask && (
        <EmailModal
          task={emailModalTask}
          onClose={() => setEmailModalTask(null)}
          onSend={(data) => {
            showToast(t('projects.toasts.emailSent', { to: data.to }));
            setEmailModalTask(null);
          }}
        />
      )}

      {/* Event Modal */}
      {eventModalTask && (
        <EventModal
          task={eventModalTask}
          onClose={() => setEventModalTask(null)}
          onCreate={(data) => {
            showToast(t('projects.toasts.eventCreated', { title: data.title }));
            setEventModalTask(null);
          }}
        />
      )}
    </div>
  );
}

interface TimesSectionProps {
  timeEntries: TimeEntry[];
  onAddTime: () => void;
  onUpdateTimeEntry?: (id: string, updates: Partial<TimeEntry>) => void;
  onDeleteTimeEntry?: (id: string) => void;
}

function TimesSection({ timeEntries, onAddTime, onUpdateTimeEntry, onDeleteTimeEntry }: TimesSectionProps) {
  const { t } = useTranslation();
  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null);
  const { showToast } = useAppShell();

  const formatMinutes = (mins: number) => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  const totalMinutes = timeEntries.reduce((sum, t) => sum + t.minutes, 0);
  const billableMinutes = timeEntries.filter(t => t.billable).reduce((sum, t) => sum + t.minutes, 0);

  const handleRowClick = (entry: TimeEntry) => {
    setEditingEntry(entry);
  };

  const handleSaveEdit = (data: { description: string; minutes: number; billable: boolean }) => {
    if (editingEntry && onUpdateTimeEntry) {
      onUpdateTimeEntry(editingEntry.id, data);
      showToast(t('projects.toasts.timeUpdated'));
    }
    setEditingEntry(null);
  };

  const handleDelete = () => {
    if (editingEntry && onDeleteTimeEntry) {
      onDeleteTimeEntry(editingEntry.id);
      showToast(t('projects.toasts.timeDeleted'));
    }
    setEditingEntry(null);
  };

  return (
    <div style={{ padding: '1rem' }}>
      {/* Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem', marginBottom: '1.5rem' }}>
        <InfoCard label={t('projects.time.totalTime')} value={formatMinutes(totalMinutes)} />
        <InfoCard label={t('projects.time.billable')} value={formatMinutes(billableMinutes)} />
      </div>

      {/* Entries */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {timeEntries.length === 0 ? (
          <div style={{ padding: '1.5rem', background: 'var(--bg)', borderRadius: '0.5rem', textAlign: 'center', color: 'var(--text-muted)' }}>
            <Timer size={24} style={{ marginBottom: '0.5rem', opacity: 0.5 }} />
            <p style={{ margin: 0 }}>{t('projects.time.noEntries')}</p>
          </div>
        ) : (
          timeEntries.map((entry) => (
            <div
              key={entry.id}
              className="editable-row data-row"
              style={{
                padding: '0.75rem', background: 'var(--bg)', borderRadius: '0.5rem',
                display: 'flex', alignItems: 'center', gap: '0.75rem',
                transition: 'background 0.15s',
              }}
            >
              <Timer size={16} style={{ color: 'var(--text-muted)' }} />
              <div
                style={{ flex: 1, cursor: 'pointer' }}
                onClick={() => handleRowClick(entry)}
              >
                <div style={{ fontWeight: 500, fontSize: '0.875rem' }}>{entry.description || t('projects.time.noDescription')}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.125rem' }}>
                  {entry.userName} • {new Date(entry.at).toLocaleDateString(undefined, { day: '2-digit', month: 'short' })}
                </div>
              </div>
              <div style={{ textAlign: 'right', marginRight: '0.5rem' }}>
                <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{formatMinutes(entry.minutes)}</div>
                {entry.billable && (
                  <span style={{ fontSize: '0.625rem', color: 'var(--success)', background: '#dcfce7', padding: '0.125rem 0.375rem', borderRadius: '9999px' }}>
                    {t('projects.time.billable')}
                  </span>
                )}
              </div>
              {/* Fast Actions */}
              <div className="data-row__actions">
                <button className="data-row__action" title={t('projects.actions.createTask')} onClick={(e) => { e.stopPropagation(); showToast(t('projects.toasts.createTaskFromTime')); }}>
                  <CheckCircle2 size={14} />
                </button>
                <button className="data-row__action" title={t('projects.actions.sendEmail')} onClick={(e) => { e.stopPropagation(); showToast(t('projects.toasts.sendEmail')); }}>
                  <Mail size={14} />
                </button>
                <button className="data-row__action" title={t('projects.actions.createEvent')} onClick={(e) => { e.stopPropagation(); showToast(t('projects.toasts.createEvent')); }}>
                  <Calendar size={14} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <button onClick={onAddTime} className="btn btn--outline" style={{ width: '100%', marginTop: '1rem' }}>
        <Plus size={16} /> {t('projects.time.log')}
      </button>

      {/* Edit Modal */}
      {editingEntry && (
        <TimeEntryEditModal
          entry={editingEntry}
          onSave={handleSaveEdit}
          onDelete={handleDelete}
          onClose={() => setEditingEntry(null)}
        />
      )}
    </div>
  );
}

/** Modal to edit a time entry */
function TimeEntryEditModal({
  entry,
  onSave,
  onDelete,
  onClose,
}: {
  entry: TimeEntry;
  onSave: (data: { description: string; minutes: number; billable: boolean }) => void;
  onDelete: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [description, setDescription] = useState(entry.description || '');
  const [hours, setHours] = useState(Math.floor(entry.minutes / 60));
  const [minutes, setMinutes] = useState(entry.minutes % 60);
  const [billable, setBillable] = useState(entry.billable);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      description,
      minutes: hours * 60 + minutes,
      billable,
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal--sm" onClick={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <h3>{t('projects.time.edit')}</h3>
          <button className="modal__close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal__body">
            {/* Date and time */}
            <div className="form-group">
              <label>{t('projects.time.date')}</label>
              <input
                type="text"
                value={new Date(entry.at).toLocaleString(undefined, {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
                disabled
                className="form-input form-input--disabled"
              />
            </div>

            {/* Duration */}
            <div className="form-group">
              <label>{t('projects.time.duration')}</label>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <input
                  type="number"
                  min="0"
                  value={hours}
                  onChange={(e) => setHours(parseInt(e.target.value) || 0)}
                  className="form-input"
                  style={{ width: '80px', textAlign: 'center' }}
                />
                <span>h</span>
                <input
                  type="number"
                  min="0"
                  max="59"
                  value={minutes}
                  onChange={(e) => setMinutes(parseInt(e.target.value) || 0)}
                  className="form-input"
                  style={{ width: '80px', textAlign: 'center' }}
                />
                <span>m</span>
              </div>
            </div>

            {/* Description */}
            <div className="form-group">
              <label>{t('projects.time.description')}</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('projects.time.descriptionPlaceholder')}
                rows={3}
                className="form-input"
              />
            </div>

            {/* Billable */}
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={billable}
                  onChange={(e) => setBillable(e.target.checked)}
                />
                <span>{t('projects.time.billable')}</span>
              </label>
            </div>
          </div>

          <div className="modal__footer">
            <button type="button" className="btn btn--danger btn--sm" onClick={onDelete}>
              {t('common.delete')}
            </button>
            <div style={{ flex: 1 }} />
            <button type="button" className="btn btn--outline btn--sm" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn btn--primary btn--sm">
              {t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function MilestonesSection({ milestones, onToggle, onAdd, onEdit }: {
  milestones: Milestone[];
  onToggle: (id: string) => void;
  onAdd: () => void;
  onEdit?: (milestone: Milestone) => void;
}) {
  const { t } = useTranslation();
  const { showToast } = useAppShell();
  const sorted = [...milestones].sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());

  const handleRowClick = (milestone: Milestone) => {
    if (onEdit) {
      onEdit(milestone);
    } else {
      showToast(`Milestone: ${milestone.title}`);
    }
  };

  return (
    <div style={{ padding: '1rem' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {sorted.length === 0 ? (
          <div style={{ padding: '1.5rem', background: 'var(--bg)', borderRadius: '0.5rem', textAlign: 'center', color: 'var(--text-muted)' }}>
            <Target size={24} style={{ marginBottom: '0.5rem', opacity: 0.5 }} />
            <p style={{ margin: 0 }}>{t('projects.milestones.none')}</p>
          </div>
        ) : (
          sorted.map((m) => (
            <div
              key={m.id}
              className="editable-row data-row"
              onClick={() => handleRowClick(m)}
              style={{
                padding: '0.75rem', background: 'var(--bg)', borderRadius: '0.5rem',
                display: 'flex', alignItems: 'center', gap: '0.75rem',
                cursor: 'pointer', transition: 'background 0.15s',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-light, #f5f5f5)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'var(--bg)')}
            >
              <button
                onClick={(e) => { e.stopPropagation(); onToggle(m.id); }}
                style={{
                  width: '24px', height: '24px', borderRadius: '50%',
                  border: `2px solid ${m.completed ? 'var(--success)' : 'var(--line)'}`,
                  background: m.completed ? 'var(--success)' : 'transparent',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {m.completed && <CheckCircle2 size={14} color="white" />}
              </button>
              <div style={{ flex: 1 }}>
                <div style={{
                  fontWeight: 500, fontSize: '0.875rem',
                  textDecoration: m.completed ? 'line-through' : 'none',
                  color: m.completed ? 'var(--text-muted)' : 'var(--text)',
                }}>
                  {m.title}
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.125rem' }}>
                  {new Date(m.dueDate).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })}
                </div>
              </div>
              {/* Fast Actions */}
              <div className="data-row__actions">
                <button className="data-row__action" title={t('projects.actions.createTask')} onClick={(e) => { e.stopPropagation(); showToast(t('projects.toasts.createTaskFromMilestone')); }}>
                  <CheckCircle2 size={14} />
                </button>
                <button className="data-row__action" title={t('projects.actions.sendEmail')} onClick={(e) => { e.stopPropagation(); showToast(t('projects.toasts.sendEmail')); }}>
                  <Mail size={14} />
                </button>
                <button className="data-row__action" title={t('projects.actions.createEvent')} onClick={(e) => { e.stopPropagation(); showToast(t('projects.toasts.createEvent')); }}>
                  <Calendar size={14} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <button onClick={onAdd} className="btn btn--outline" style={{ width: '100%', marginTop: '1rem' }}>
        <Plus size={16} /> {t('projects.milestones.add')}
      </button>
    </div>
  );
}

function InfoCard({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ padding: '0.75rem', background: 'var(--bg)', borderRadius: '0.5rem' }}>
      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>{label}</div>
      <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{value}</div>
    </div>
  );
}

// =============================================================================
// TEAM EDIT MODAL
// =============================================================================

interface TeamEditModalProps {
  team: TeamMember[];
  onSave: (team: TeamMember[]) => void;
  onClose: () => void;
}

/** Team member card component */
function TeamMemberCard({
  member,
  defaultRole,
  isInProject,
  customRole,
  onToggle,
  onRoleChange,
}: {
  member: { id: string; name: string; role: string };
  defaultRole: string;
  isInProject: boolean;
  customRole?: string;
  onToggle: () => void;
  onRoleChange?: (role: string) => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '0.75rem',
        background: 'var(--bg)',
        borderRadius: '0.5rem',
        position: 'relative',
        minWidth: '100px',
        opacity: isInProject ? 1 : 0.7,
      }}
    >
      {/* X o + button in alto a sinistra */}
      <button
        onClick={onToggle}
        style={{
          position: 'absolute',
          top: '0.25rem',
          left: '0.25rem',
          width: '20px',
          height: '20px',
          borderRadius: '50%',
          border: 'none',
          background: isInProject ? 'var(--danger, #ef4444)' : 'var(--success, #22c55e)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: 600,
        }}
        title={isInProject ? t('projects.team.removeFromProject') : t('projects.team.addToProject')}
      >
        {isInProject ? <X size={12} /> : <Plus size={12} />}
      </button>

      {/* Avatar */}
      <div
        style={{
          width: '48px',
          height: '48px',
          borderRadius: '50%',
          background: 'var(--brand)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '1.125rem',
          fontWeight: 600,
          marginBottom: '0.5rem',
        }}
      >
        {member.name?.[0]?.toUpperCase() || '?'}
      </div>

      {/* Name */}
      <div
        style={{
          fontWeight: 500,
          fontSize: '0.8125rem',
          textAlign: 'center',
          marginBottom: '0.25rem',
        }}
      >
        {member.name}
      </div>

      {/* Campo ruolo con placeholder */}
      {isInProject ? (
        <input
          type="text"
          value={customRole || ''}
          onChange={(e) => onRoleChange?.(e.target.value)}
          placeholder={defaultRole}
          style={{
            width: '100%',
            padding: '0.25rem 0.5rem',
            border: '1px solid var(--line)',
            borderRadius: '0.25rem',
            fontSize: '0.6875rem',
            textAlign: 'center',
            background: 'transparent',
          }}
        />
      ) : (
        <div
          style={{
            fontSize: '0.6875rem',
            color: 'var(--text-muted)',
            textAlign: 'center',
          }}
        >
          {defaultRole}
        </div>
      )}
    </div>
  );
}

function TeamEditModal({ team, onSave, onClose }: TeamEditModalProps) {
  const { t } = useTranslation();
  // Get team members from settings store
  const teamMembers = useSettingsStore((state) => state.teamMembers);

  // State for project members (with custom roles)
  const [projectMembers, setProjectMembers] = useState<Map<string, string>>(() => {
    // Initialize with current project members
    const map = new Map<string, string>();
    team.forEach((m) => {
      map.set(m.userId, m.role || '');
    });
    return map;
  });

  // Convert global role to readable format
  const getRoleLabel = (role: string): string => {
    const roleLabels: Record<string, string> = {
      owner: t('projects.team.roles.owner'),
      admin: t('projects.team.roles.admin'),
      member: t('projects.team.roles.member'),
      viewer: t('projects.team.roles.viewer'),
    };
    return roleLabels[role] || role || t('projects.team.roles.member');
  };

  const handleToggleMember = (userId: string, defaultRole: string) => {
    setProjectMembers((prev) => {
      const next = new Map(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.set(userId, ''); // Ruolo vuoto = usa quello di default
      }
      return next;
    });
  };

  const handleRoleChange = (userId: string, role: string) => {
    setProjectMembers((prev) => {
      const next = new Map(prev);
      next.set(userId, role);
      return next;
    });
  };

  const handleSave = () => {
    // Converti la mappa in array di TeamMember
    const newTeam: TeamMember[] = [];
    projectMembers.forEach((customRole, userId) => {
      const globalMember = teamMembers.find((m) => m.id === userId);
      if (globalMember) {
        newTeam.push({
          userId,
          role: customRole || getRoleLabel(globalMember.role),
          displayName: globalMember.name,
        });
      }
    });
    onSave(newTeam);
  };

  // Separate members in project and not in project
  const membersInProject = teamMembers.filter((m) => projectMembers.has(m.id));
  const membersNotInProject = teamMembers.filter((m) => !projectMembers.has(m.id));

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <h3>{t('projects.team.manage')}</h3>
          <button className="modal__close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="modal__body">
          {/* Members in project */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label
              style={{
                display: 'block',
                fontWeight: 500,
                marginBottom: '0.75rem',
                fontSize: '0.875rem',
              }}
            >
              {t('projects.team.projectTeam')}
            </label>
            {membersInProject.length === 0 ? (
              <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                {t('projects.team.noMembersAssigned')}
              </p>
            ) : (
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '0.75rem',
                }}
              >
                {membersInProject.map((member) => (
                  <TeamMemberCard
                    key={member.id}
                    member={{ id: member.id, name: member.name, role: member.role }}
                    defaultRole={getRoleLabel(member.role)}
                    isInProject={true}
                    customRole={projectMembers.get(member.id) || ''}
                    onToggle={() => handleToggleMember(member.id, getRoleLabel(member.role))}
                    onRoleChange={(role) => handleRoleChange(member.id, role)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Members not in project */}
          {membersNotInProject.length > 0 && (
            <div>
              <label
                style={{
                  display: 'block',
                  fontWeight: 500,
                  marginBottom: '0.75rem',
                  fontSize: '0.875rem',
                  color: 'var(--text-secondary)',
                }}
              >
                {t('projects.team.otherMembers')}
              </label>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '0.75rem',
                }}
              >
                {membersNotInProject.map((member) => (
                  <TeamMemberCard
                    key={member.id}
                    member={{ id: member.id, name: member.name, role: member.role }}
                    defaultRole={getRoleLabel(member.role)}
                    isInProject={false}
                    onToggle={() => handleToggleMember(member.id, getRoleLabel(member.role))}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Messaggio se non ci sono membri nel team globale */}
          {teamMembers.length === 0 && (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', textAlign: 'center' }}>
              {t('projects.team.noTeamMembers')}
            </p>
          )}
        </div>

        <div className="modal__footer">
          <button className="btn btn--outline" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button className="btn btn--primary" onClick={handleSave}>
            {t('projects.team.save')}
          </button>
        </div>
      </div>
    </div>
  );
}

function EconomiaSection({ projectId, project, timeEntries }: {
  projectId: string;
  project: Project;
  timeEntries: TimeEntry[];
}) {
  const { t } = useTranslation();
  const { openPrompt, showToast } = useAppShell();
  const {
    snapshot,
    projectInvoices: invoices,
    unbilledEntries,
    draftProposals,
    createDraftProposal,
    convertProposalToInvoice,
    deleteDraftProposal,
  } = useBridge(projectId);

  const billableRevenue = calculateBillableRevenue(timeEntries);
  const unbilledRevenue = calculateBillableRevenue(unbilledEntries);

  const handleCreateProposal = () => {
    const proposal = createDraftProposal();
    if (proposal) {
      showToast(t('projects.economy.proposalCreated'));
    } else {
      showToast(t('projects.economy.noBillableTime'));
    }
  };

  const handleConvertProposal = async (proposalId: string) => {
    try {
      const invoiceId = await convertProposalToInvoice(
        proposalId,
        project.clientName || 'Client'
      );
      if (invoiceId) {
        showToast(t('projects.economy.invoiceCreated'));
      }
    } catch (err) {
      console.error('Failed to convert proposal to invoice', err);
      showToast(t('projects.economy.invoiceCreateFailed'));
    }
  };

  const handleCreateInvoice = () => {
    openPrompt(t('projects.economy.createInvoicePrompt', { title: project.title }));
  };

  return (
    <div style={{ padding: '1rem' }}>
      {/* Snapshot economico */}
      {snapshot && (
        <>
          <div style={{ fontWeight: 600, marginBottom: '0.75rem' }}>{t('projects.economy.summary')}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem', marginBottom: '1.5rem' }}>
            <InfoCard label={t('projects.economy.totalCosts')} value={formatEUR(snapshot.costTotal)} />
            <InfoCard label={t('projects.economy.potentialRevenue')} value={formatEUR(snapshot.revenuePotential)} />
            <InfoCard label={t('projects.economy.invoiced')} value={formatEUR(snapshot.revenueInvoiced)} />
            <InfoCard
              label={t('projects.economy.margin')}
              value={formatEUR(snapshot.margin)}
            />
          </div>

          {/* Burn rate e previsioni */}
          {snapshot.burnRate > 0 && (
            <div style={{ padding: '0.75rem', background: 'var(--bg)', borderRadius: '0.5rem', marginBottom: '1.5rem' }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>
                {t('projects.economy.avgBurnRate')}
              </div>
              <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>
                {formatEUR(snapshot.burnRate)} {t('projects.economy.perHour')}
              </div>
              {snapshot.forecastFinishCost && (
                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                  {t('projects.economy.estimateToComplete', { costs: formatEUR(snapshot.forecastFinishCost), revenue: formatEUR(snapshot.forecastFinishRevenue || 0) })}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Non fatturato */}
      {unbilledEntries.length > 0 && (
        <div style={{
          padding: '0.75rem', marginBottom: '1.5rem',
          background: '#fef3c7', borderRadius: '0.5rem',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 600, color: '#f59e0b' }}>
                {formatEUR(unbilledRevenue)} {t('projects.economy.toBill')}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#92400e' }}>
                {t('projects.economy.unbilledEntries', { count: unbilledEntries.length })}
              </div>
            </div>
            <button
              onClick={handleCreateProposal}
              style={{
                padding: '0.375rem 0.75rem', border: 'none', borderRadius: '0.375rem',
                background: '#f59e0b', color: 'white', fontWeight: 500, cursor: 'pointer',
                fontSize: '0.8125rem',
              }}
            >
              {t('projects.economy.generateProposal')}
            </button>
          </div>
        </div>
      )}

      {/* Proposte fattura in attesa */}
      {draftProposals.length > 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>{t('projects.economy.invoiceProposals')}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {draftProposals.map((proposal) => (
              <div key={proposal.id} style={{
                padding: '0.75rem', background: 'var(--bg)', borderRadius: '0.5rem',
                border: '1px dashed var(--brand)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>
                      {t('projects.economy.draft')} #{proposal.suggestedNumber}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      {proposal.notes.join(' • ')}
                    </div>
                  </div>
                  <div style={{ fontWeight: 600, color: 'var(--brand)' }}>
                    {formatEUR(proposal.total)}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => handleConvertProposal(proposal.id)}
                    style={{
                      flex: 1, padding: '0.375rem', border: 'none', borderRadius: '0.375rem',
                      background: 'var(--brand)', color: 'white', fontWeight: 500, cursor: 'pointer',
                      fontSize: '0.8125rem',
                    }}
                  >
                    <FileText size={14} style={{ marginRight: '0.25rem', verticalAlign: 'middle' }} />
                    {t('projects.economy.createInvoice')}
                  </button>
                  <button
                    onClick={() => deleteDraftProposal(proposal.id)}
                    style={{
                      padding: '0.375rem 0.75rem', border: '1px solid var(--line)', borderRadius: '0.375rem',
                      background: 'transparent', cursor: 'pointer', fontSize: '0.8125rem',
                    }}
                  >
                    {t('common.delete')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Project invoices */}
      <div style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
          <div style={{ fontWeight: 600 }}>{t('projects.economy.linkedInvoices')}</div>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            {t('projects.economy.invoicesCount', { count: invoices.length })}
          </span>
        </div>

        {invoices.length === 0 ? (
          <div style={{ padding: '1.5rem', background: 'var(--bg)', borderRadius: '0.5rem', textAlign: 'center', color: 'var(--text-muted)' }}>
            <Receipt size={24} style={{ marginBottom: '0.5rem', opacity: 0.5 }} />
            <p style={{ margin: 0 }}>{t('projects.economy.noLinkedInvoices')}</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {invoices.map((inv) => (
              <div key={inv.id} style={{
                padding: '0.75rem', background: 'var(--bg)', borderRadius: '0.5rem',
                display: 'flex', alignItems: 'center', gap: '0.75rem',
              }}>
                <div style={{
                  width: '32px', height: '32px', borderRadius: '50%',
                  background: inv.direction === 'issued' ? '#dcfce7' : '#fef3c7',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {inv.direction === 'issued'
                    ? <ArrowUpRight size={16} color="#22c55e" />
                    : <ArrowDownLeft size={16} color="#f59e0b" />
                  }
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 500, fontSize: '0.875rem' }}>{inv.number}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                    {inv.counterparty} • {new Date(inv.at).toLocaleDateString(undefined, { day: '2-digit', month: 'short' })}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{formatEUR(inv.total)}</div>
                  <span style={{
                    fontSize: '0.625rem', padding: '0.125rem 0.375rem', borderRadius: '9999px',
                    background: inv.status === 'paid' ? '#dcfce7' : inv.status === 'overdue' ? '#fef3c7' : 'var(--line)',
                    color: inv.status === 'paid' ? '#22c55e' : inv.status === 'overdue' ? '#f59e0b' : 'var(--text-secondary)',
                  }}>
                    {inv.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <button onClick={handleCreateInvoice} className="btn btn--outline" style={{ width: '100%' }}>
        <Plus size={16} /> {t('projects.economy.newInvoice')}
      </button>
    </div>
  );
}

// =============================================================================
// MAIN VIEW
// =============================================================================

interface ProjectsViewProps {
  projects?: ProjectItem[];
  onProjectClick?: (project: ProjectItem) => void;
}

// Valid project filters for list view (status + health)
const VALID_LIST_FILTERS = ['all', 'attivo', 'archiviato', 'da-iniziare', 'in-corso', 'pericolo', 'ritardo'];

export function ProjectsView({ projects: propProjects, onProjectClick }: ProjectsViewProps) {
  const { activeFilters, setActiveFilter } = useAppShell();
  const { setActiveViewKey } = useProjectsStore();
  const location = useLocation();
  const { goToProject, goToProjects } = useAppNavigate();

  // Parse projectId from URL directly (useParams doesn't work when rendered by AppShell)
  const projectId = useMemo(() => {
    const match = location.pathname.match(/\/(?:progetti|projects)\/([^/]+)/);
    return match ? match[1] : undefined;
  }, [location.pathname]);

  // Sync filters - only use valid list filters when in list view
  useEffect(() => {
    if (!projectId) {
      const filter = activeFilters.progetti || 'all';
      // Only apply valid project status filters
      const validFilter = VALID_LIST_FILTERS.includes(filter) ? filter : 'all';
      setActiveViewKey(validFilter);
      // Reset to all if the filter was invalid
      if (filter !== validFilter) {
        setActiveFilter('progetti', 'all');
      }
    }
  }, [activeFilters.progetti, setActiveViewKey, projectId, setActiveFilter]);

  const handleSelectProject = useCallback((id: string) => {
    goToProject(id);
  }, [goToProject]);

  const handleBack = useCallback(() => {
    goToProjects();
    setActiveFilter('progetti', 'all'); // Reset filter to show all projects
  }, [goToProjects, setActiveFilter]);

  // If a project is selected via URL, show detail view
  if (projectId) {
    return <ProjectDetailView projectId={projectId} onBack={handleBack} />;
  }

  // Otherwise show list view
  return <ProjectListView onSelectProject={handleSelectProject} />;
}
